<template>
  <div class="bg-background min-h-screen pb-10" @click="sectionDropdownOpen = false">
    <!-- Page Header -->
    <div class="bg-card border-b border-border">
      <div class="w-full px-4 py-4 sm:py-5">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <i class="fas fa-gear text-primary text-lg"></i>
          </div>
          <div>
            <h1 class="text-xl font-bold leading-tight">Account Settings</h1>
            <p class="text-sm text-muted-foreground leading-snug">Manage your profile, password, integrations, and subscription.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <Toast
      :show="showToast"
      :type="toastType"
      :title="toastTitle"
      :message="toastMessage"
      @close="showToast = false"
    />

    <!-- Settings Content -->
    <div class="w-full px-3 sm:px-4 py-4 sm:py-8">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
        <!-- Settings Navigation Sidebar -->
        <div class="md:col-span-1">
          <div class="bg-card rounded-xl border border-border shadow-sm overflow-visible md:overflow-hidden md:sticky md:top-20">
            <!-- Mobile nav: dropdown -->
            <div class="md:hidden p-2 relative" @click.stop>
              <button @click="sectionDropdownOpen = !sectionDropdownOpen"
                class="w-full h-10 pl-3 pr-9 rounded-lg border border-border bg-card text-sm font-medium flex items-center gap-2.5 hover:bg-muted transition text-left relative">
                <i :class="[computedSettingsSections.find(s => s.id === activeSection)?.icon || 'fas fa-gear', 'text-sm text-primary flex-shrink-0']"></i>
                <span class="flex-1">{{ computedSettingsSections.find(s => s.id === activeSection)?.title || 'Settings' }}</span>
                <i class="fas fa-chevron-down text-[10px] text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 transition-transform duration-150"
                  :class="sectionDropdownOpen ? 'rotate-180' : ''"></i>
              </button>
              <transition name="fade">
                <div v-if="sectionDropdownOpen"
                  class="absolute top-[calc(100%-4px)] left-2 right-2 z-50 bg-card border border-border rounded-xl shadow-lg py-1 overflow-hidden">
                  <button
                    v-for="(section, index) in computedSettingsSections"
                    :key="'m-' + index"
                    @click="section.route ? ($router.push(section.route), sectionDropdownOpen = false) : (activeSection = section.id, sectionDropdownOpen = false)"
                    class="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-muted transition text-left"
                    :class="activeSection === section.id ? 'text-foreground' : 'text-muted-foreground'">
                    <i :class="[section.icon, 'text-sm w-4 text-center flex-shrink-0', activeSection === section.id ? 'text-primary' : '']"></i>
                    <span class="flex-1 font-medium">{{ section.title }}</span>
                    <i v-if="activeSection === section.id" class="fas fa-check text-primary text-xs"></i>
                  </button>
                </div>
              </transition>
            </div>
            <!-- Tablet/desktop nav: vertical list -->
            <nav class="hidden md:flex md:flex-col">
              <button
                v-for="(section, index) in computedSettingsSections"
                :key="'d-' + index"
                @click="section.route ? $router.push(section.route) : activeSection = section.id"
                :class="[
                  'py-3 px-4 text-left border-l-2 transition-colors',
                  activeSection === section.id
                    ? 'border-primary bg-primary/5 text-primary font-medium'
                    : 'border-transparent hover:bg-accent text-muted-foreground hover:text-foreground',
                ]"
              >
                <div class="flex items-center">
                  <i :class="['mr-3 w-5 text-center text-sm', section.icon]"></i>
                  <span class="text-sm">{{ section.title }}</span>
                </div>
              </button>
            </nav>
          </div>
        </div>

        <!-- Settings Content -->
        <div class="md:col-span-3 min-w-0">
          <!-- Loading State -->
          <div v-if="loading" class="space-y-6">
            <div class="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
              <div class="p-6 border-b border-border">
                <div class="h-5 w-52 rounded bg-muted animate-pulse"></div>
                <div class="mt-2 h-4 w-72 rounded bg-muted animate-pulse"></div>
              </div>
              <div class="p-6 space-y-5">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="h-10 rounded-lg bg-muted animate-pulse"></div>
                  <div class="h-10 rounded-lg bg-muted animate-pulse"></div>
                </div>
                <div class="h-10 rounded-lg bg-muted animate-pulse"></div>
                <div class="h-10 rounded-lg bg-muted animate-pulse"></div>
                <div class="flex justify-end">
                  <div class="h-10 w-36 rounded-lg bg-muted animate-pulse"></div>
                </div>
              </div>
            </div>

            <div class="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
              <div class="p-6 border-b border-border">
                <div class="h-5 w-44 rounded bg-muted animate-pulse"></div>
                <div class="mt-2 h-4 w-80 rounded bg-muted animate-pulse"></div>
              </div>
              <div class="p-6 space-y-5">
                <div class="h-10 rounded-lg bg-muted animate-pulse"></div>
                <div class="h-10 rounded-lg bg-muted animate-pulse"></div>
                <div class="flex justify-end">
                  <div class="h-10 w-36 rounded-lg bg-muted animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Error Alert -->
          <div
            v-if="error"
            class="bg-red-500/10 border-l-4 border-red-500 p-4 mb-6 rounded-md"
          >
            <div class="flex">
              <div class="flex-shrink-0">
                <i class="fas fa-exclamation-circle text-red-600 dark:text-red-400"></i>
              </div>
              <div class="ml-3">
                <p class="text-sm text-red-700 dark:text-red-300">{{ error }}</p>
              </div>
            </div>
          </div>

          <!-- Personal Information Section -->
          <div v-if="activeSection === 'personal' && !loading" class="space-y-4 sm:space-y-6">
            <form @submit.prevent="savePersonalInfo" class="space-y-4 sm:space-y-6">
              <div class="bg-card rounded-xl border border-border shadow-sm overflow-hidden transition duration-200 hover:shadow-md">
                <div class="p-4 sm:p-6 border-b border-border bg-muted/20">
                  <h2 class="text-base sm:text-lg font-medium text-foreground">
                    Account Information
                  </h2>
                  <p class="text-xs sm:text-sm text-muted-foreground mt-1">
                    Update your personal details
                  </p>
                </div>

                <div class="p-4 sm:p-6">
                  <div class="space-y-5 sm:space-y-6">
                    <!-- Profile Picture -->
                    <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/50 p-3 sm:justify-center sm:bg-transparent sm:border-0 sm:p-0">
                      <div class="relative shrink-0">
                        <div
                          v-if="userSettings.profile_picture"
                          class="profile-avatar h-16 w-16 sm:h-24 sm:w-24 bg-card rounded-full flex items-center justify-center text-primary text-2xl sm:text-3xl font-bold shadow-md relative overflow-hidden"
                        >
                          <img
                            :src="userSettings.profile_picture"
                            :alt="userSettings.first_name || 'Profile'"
                            class="w-full h-full object-cover rounded-full"
                            @error="handleImageError"
                          />
                        </div>
                        <div
                          v-else
                          class="profile-avatar h-16 w-16 sm:h-24 sm:w-24 bg-card sm:bg-transparent border-2 border-border rounded-full flex items-center justify-center text-primary text-2xl sm:text-3xl font-bold shadow-md relative overflow-hidden"
                        >
                          <i class="fas fa-user text-blue-500"></i>
                        </div>
                      </div>
                      <div class="sm:hidden min-w-0">
                        <p class="text-sm font-semibold text-foreground">
                          {{ userSettings.first_name || userSettings.username || 'Your profile' }}
                        </p>
                        <p class="text-xs text-muted-foreground">Profile photo and account identity</p>
                      </div>
                    </div>

                    <!-- First Name & Last Name -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label
                          for="firstName"
                          class="block text-xs sm:text-sm font-medium text-muted-foreground mb-1"
                          >First Name</label
                        >
                        <input
                          v-model="userSettings.first_name"
                          id="firstName"
                          type="text"
                          class="w-full px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg border-input bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          placeholder="Enter your first name"
                        />
                      </div>

                      <div>
                        <label
                          for="lastName"
                          class="block text-xs sm:text-sm font-medium text-muted-foreground mb-1"
                          >Last Name</label
                        >
                        <input
                          v-model="userSettings.last_name"
                          id="lastName"
                          type="text"
                          class="w-full px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg border-input bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          placeholder="Enter your last name"
                        />
                      </div>
                    </div>

                    <!-- Username -->
                    <div>
                      <label
                        for="username"
                        class="block text-xs sm:text-sm font-medium text-muted-foreground mb-1"
                        >Username</label
                      >
                      <input
                        v-model="userSettings.username"
                        id="username"
                        type="text"
                        class="w-full px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg border-input bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                        placeholder="Enter your username"
                      />
                      <p class="mt-1 text-xs text-muted-foreground">
                        This will be your public profile name
                      </p>
                    </div>

                    <!-- Email & Phone -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label
                          for="email"
                          class="block text-xs sm:text-sm font-medium text-muted-foreground mb-1"
                          >Email Address</label
                        >
                        <input
                          v-model="userSettings.email"
                          id="email"
                          type="email"
                          class="w-full px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg border-input bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          placeholder="Enter your email"
                          required
                        />
                        <p class="mt-1 text-xs text-muted-foreground">
                          We'll send important notifications to this email
                        </p>
                      </div>

                      <div>
                        <label
                          for="phone"
                          class="block text-xs sm:text-sm font-medium text-muted-foreground mb-1"
                          >Phone Number</label
                        >
                        <input
                          v-model="userSettings.phone_number"
                          id="phone"
                          type="tel"
                          class="w-full px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg border-input bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          placeholder="Enter your phone number"
                        />
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              <!-- Save Button -->
              <div class="flex justify-end">
                <button
                  type="submit"
                  class="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2.5 sm:py-2 bg-primary hover:bg-primary-dark text-primary-foreground rounded-lg transition duration-200 text-sm"
                  :disabled="personalInfoSaving"
                >
                  <span v-if="personalInfoSaving">
                    <i class="fas fa-spinner fa-spin mr-2"></i> Saving...
                  </span>
                  <span v-else>Save Changes</span>
                </button>
              </div>
            </form>

            <!-- Danger Zone -->
            <div class="bg-card rounded-xl border border-red-500/30 shadow-sm overflow-hidden">
              <div class="p-4 sm:p-6 border-b border-red-500/20 bg-red-500/10">
                <h2 class="text-base sm:text-lg font-medium text-red-700 dark:text-red-300">Danger Zone</h2>
                <p class="text-xs sm:text-sm text-red-600/80 dark:text-red-400/80 mt-0.5">Irreversible and destructive actions</p>
              </div>
              <div class="p-4 sm:p-6">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p class="text-sm font-medium text-foreground">Delete Account</p>
                    <p class="text-xs text-muted-foreground mt-0.5">
                      Your account will be permanently deactivated and you will no longer be able to log in.
                    </p>
                  </div>
                  <button
                    type="button"
                    @click="showDeleteAccountModal = true"
                    class="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 bg-card border border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-500/10 hover:border-red-500/50 rounded-lg text-sm font-medium transition-colors"
                  >
                    <i class="fas fa-trash-alt"></i>
                    Delete Account
                  </button>
                </div>
              </div>
            </div>

          </div>

          <!-- Security Section -->
          <div v-if="activeSection === 'security' && !loading" class="space-y-4 sm:space-y-6">
            <div class="bg-card rounded-xl border border-border shadow-sm overflow-hidden transition duration-200 hover:shadow-md">
              <div class="p-4 sm:p-6 border-b border-border bg-muted/20">
                <h2 class="text-base sm:text-lg font-medium text-foreground">Update Password</h2>
                <p class="text-xs sm:text-sm text-muted-foreground mt-1">
                  Ensure your account is using a secure password
                </p>
              </div>

              <div class="p-4 sm:p-6">
                <form @submit.prevent="updatePassword">
                  <div class="space-y-6">
                    <!-- Current Password -->
                    <div>
                      <label
                        for="currentPassword"
                        class="block text-xs sm:text-sm font-medium text-muted-foreground mb-1"
                        >Current Password</label
                      >
                      <div class="relative">
                        <input
                          v-model="passwordForm.currentPassword"
                          id="currentPassword"
                          :type="showCurrentPassword ? 'text' : 'password'"
                          class="w-full px-3 sm:px-4 py-2.5 sm:py-2 pr-11 rounded-lg border-input bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          placeholder="Enter your current password"
                          required
                        />
                        <button
                          type="button"
                          class="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                          @click="showCurrentPassword = !showCurrentPassword"
                          :aria-label="showCurrentPassword ? 'Hide current password' : 'Show current password'"
                        >
                          <i :class="showCurrentPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                        </button>
                      </div>
                    </div>

                    <!-- New Password -->
                    <div>
                      <label
                        for="newPassword"
                        class="block text-xs sm:text-sm font-medium text-muted-foreground mb-1"
                        >New Password</label
                      >
                      <div class="relative">
                        <input
                          v-model="passwordForm.newPassword"
                          id="newPassword"
                          :type="showNewPassword ? 'text' : 'password'"
                          class="w-full px-3 sm:px-4 py-2.5 sm:py-2 pr-11 rounded-lg border-input bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          placeholder="Enter your new password"
                          required
                        />
                        <button
                          type="button"
                          class="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                          @click="showNewPassword = !showNewPassword"
                          :aria-label="showNewPassword ? 'Hide new password' : 'Show new password'"
                        >
                          <i :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                        </button>
                      </div>
                      <div v-if="passwordForm.newPassword" class="mt-3 rounded-xl border border-border bg-muted/40 p-3">
                        <div class="flex items-center justify-between">
                          <span class="text-xs font-medium text-muted-foreground">Strength</span>
                          <span
                            class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide"
                            :class="passwordStrengthClass"
                          >
                            {{ passwordStrength.label }}
                          </span>
                        </div>

                        <div class="mt-2 h-2 w-full rounded-full bg-muted overflow-hidden">
                          <div
                            class="h-full rounded-full transition-all duration-300"
                            :class="passwordStrengthBarClass"
                            :style="{ width: passwordStrengthPercent + '%' }"
                          ></div>
                        </div>

                        <div class="mt-3 grid grid-cols-1 gap-1">
                          <div class="flex items-center text-xs">
                            <span
                              class="mr-2 inline-flex h-4 w-4 items-center justify-center rounded-full"
                              :class="passwordRequirements.length ? 'bg-green-500/15 text-green-700 dark:text-green-300' : 'bg-muted text-muted-foreground'"
                            >
                              <i :class="passwordRequirements.length ? 'fas fa-check text-[10px]' : 'fas fa-minus text-[10px]'" />
                            </span>
                            <span :class="passwordRequirements.length ? 'text-foreground' : 'text-muted-foreground'">
                              At least 8 characters
                            </span>
                          </div>
                          <div class="flex items-center text-xs">
                            <span
                              class="mr-2 inline-flex h-4 w-4 items-center justify-center rounded-full"
                              :class="passwordRequirements.uppercase ? 'bg-green-500/15 text-green-700 dark:text-green-300' : 'bg-muted text-muted-foreground'"
                            >
                              <i :class="passwordRequirements.uppercase ? 'fas fa-check text-[10px]' : 'fas fa-minus text-[10px]'" />
                            </span>
                            <span :class="passwordRequirements.uppercase ? 'text-foreground' : 'text-muted-foreground'">
                              One uppercase letter
                            </span>
                          </div>
                          <div class="flex items-center text-xs">
                            <span
                              class="mr-2 inline-flex h-4 w-4 items-center justify-center rounded-full"
                              :class="passwordRequirements.number ? 'bg-green-500/15 text-green-700 dark:text-green-300' : 'bg-muted text-muted-foreground'"
                            >
                              <i :class="passwordRequirements.number ? 'fas fa-check text-[10px]' : 'fas fa-minus text-[10px]'" />
                            </span>
                            <span :class="passwordRequirements.number ? 'text-foreground' : 'text-muted-foreground'">
                              One number
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Confirm New Password -->
                    <div>
                      <label
                        for="confirmPassword"
                        class="block text-xs sm:text-sm font-medium text-muted-foreground mb-1"
                        >Confirm New Password</label
                      >
                      <div class="relative">
                        <input
                          v-model="passwordForm.confirmPassword"
                          id="confirmPassword"
                          :type="showConfirmPassword ? 'text' : 'password'"
                          class="w-full px-3 sm:px-4 py-2.5 sm:py-2 pr-11 rounded-lg border-input bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          placeholder="Confirm your new password"
                          required
                        />
                        <button
                          type="button"
                          class="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                          @click="showConfirmPassword = !showConfirmPassword"
                          :aria-label="showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'"
                        >
                          <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                        </button>
                      </div>
                    </div>

                    <!-- Save Button -->
                    <div class="flex justify-end">
                      <button
                        type="submit"
                        class="w-full sm:w-auto inline-flex items-center justify-center px-6 py-2.5 sm:py-2 bg-primary hover:bg-primary-dark text-primary-foreground rounded-lg transition duration-200 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                        :disabled="passwordSaving || !isPasswordStrongEnough"
                      >
                        <span v-if="passwordSaving">
                          <i class="fas fa-spinner fa-spin mr-2"></i> Updating...
                        </span>
                        <span v-else>Update Password</span>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <!-- Integrations Section -->
          <div
            v-if="activeSection === 'integrations' && !loading"
            class="bg-card rounded-xl border border-border shadow-sm overflow-hidden mb-6"
          >
            <div class="p-4 sm:p-6 border-b border-border">
              <div class="flex items-start justify-between gap-4">
                <div class="min-w-0">
                  <h2 class="text-lg font-medium text-foreground">
                    External Integrations
                  </h2>
                  <p class="text-sm text-muted-foreground mt-1">
                    Connect your job platform accounts for seamless application
                    syncing
                  </p>
                </div>
                <div
                  class="h-10 w-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg"
                >
                  <i class="fas fa-plug text-white text-sm"></i>
                </div>
              </div>
            </div>

            <div class="p-4 sm:p-6">
              <!-- OLJ Integration Card: only shown once admin enables OLJ for this user -->
              <div
                v-if="oljUnlocked"
                class="relative overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:shadow-lg"
                :class="{
                  'ring-2 ring-green-500/20': integrations.olj_connected && !oljNeedsReconnect,
                  'ring-2 ring-amber-500/30': oljNeedsReconnect,
                }"
              >
                <!-- Status Banner -->
                <div
                  v-if="oljNeedsReconnect"
                  class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-500"
                ></div>
                <div
                  v-else-if="integrations.olj_connected"
                  class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500"
                ></div>
                <div
                  v-else
                  class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300"
                ></div>

                <div class="p-4 sm:p-6">
                  <!-- Header -->
                  <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div class="flex items-start gap-3 sm:gap-4 min-w-0">
                      <div class="relative">
                        <div
                          class="h-12 w-12 sm:h-16 sm:w-16 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30"
                        >
                          <i class="fas fa-briefcase text-white text-lg sm:text-2xl"></i>
                        </div>
                        <div
                          v-if="oljNeedsReconnect"
                          class="absolute -bottom-1 -right-1 h-6 w-6 bg-amber-500 rounded-full border-2 border-white flex items-center justify-center shadow-sm"
                        >
                          <i class="fas fa-exclamation text-white text-xs"></i>
                        </div>
                        <div
                          v-else-if="integrations.olj_connected"
                          class="absolute -bottom-1 -right-1 h-6 w-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center shadow-sm"
                        >
                          <i class="fas fa-check text-white text-xs"></i>
                        </div>
                      </div>
                      <div class="min-w-0">
                        <h3 class="text-lg font-semibold text-foreground">
                          OnlineJobs.ph
                        </h3>
                        <p class="text-sm text-muted-foreground leading-snug">
                          Philippines' largest remote job platform
                        </p>
                        <div class="flex flex-wrap items-center gap-2 mt-2">
                          <span
                            :class="[
                              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                              integrations.olj_connected && !oljNeedsReconnect
                                ? 'bg-green-500/15 text-green-700 dark:text-green-300 border border-green-500/30'
                                : 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30',
                            ]"
                            data-test="olj-connection-status"
                          >
                            <span v-if="oljNeedsReconnect" class="flex items-center">
                              <i class="fas fa-exclamation-triangle mr-1.5 text-[10px]"></i>
                              Needs reconnect
                            </span>
                            <span
                              v-else-if="integrations.olj_connected"
                              class="flex items-center"
                            >
                              <span
                                class="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5 animate-pulse"
                              ></span>
                              Connected
                            </span>
                            <span v-else class="flex items-center">
                              <span
                                class="w-1.5 h-1.5 bg-amber-500 rounded-full mr-1.5"
                              ></span>
                              Not Connected
                            </span>
                          </span>
                          <span
                            v-if="
                              integrations.olj_connected &&
                              integrations.olj_id_proof !== null &&
                              integrations.olj_id_proof !== undefined
                            "
                            class="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30"
                          >
                            <i
                              class="fas fa-shield-alt mr-1.5 text-blue-600 dark:text-blue-400"
                            ></i>
                            ID Proof: {{ integrations.olj_id_proof }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Action Button -->
                    <div v-if="integrations.olj_connected" class="sm:shrink-0">
                      <button
                        @click="showDisconnectOljModal = true"
                        class="group flex w-full sm:w-auto items-center justify-center gap-2 px-4 py-2 bg-card border border-red-500/30 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-500/10 hover:border-red-500/50 transition-all duration-200 shadow-sm"
                      >
                        <i
                          class="fas fa-unlink text-sm group-hover:rotate-12 transition-transform"
                        ></i>
                        <span class="text-sm font-medium">Disconnect</span>
                      </button>
                    </div>
                  </div>

                  <!-- Connected State -->
                  <div v-if="integrations.olj_connected" class="mt-6 space-y-4">
                    <!-- Saved login rejected by OLJ -->
                    <div
                      v-if="oljNeedsReconnect"
                      class="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 sm:p-4"
                      data-test="olj-reconnect-warning"
                    >
                      <div class="flex flex-col gap-3 sm:flex-row sm:items-start">
                        <i class="fas fa-exclamation-triangle mt-0.5 text-amber-600 dark:text-amber-400"></i>
                        <div class="min-w-0 flex-1">
                          <p class="text-sm font-semibold text-amber-800 dark:text-amber-200">
                            Your OnlineJobs.ph login stopped working
                          </p>
                          <p
                            v-if="integrations.olj_connection_status_reason"
                            class="mt-0.5 text-sm text-amber-800/90 dark:text-amber-200/90"
                          >
                            {{ integrations.olj_connection_status_reason }}
                          </p>
                          <p class="mt-1 text-xs text-amber-700 dark:text-amber-300">
                            Auto Apply is paused and applications won't go through until you reconnect.
                          </p>
                        </div>
                        <button
                          type="button"
                          @click="openUpdateOljForm"
                          class="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-amber-600 sm:w-auto sm:shrink-0"
                          data-test="olj-reconnect-button"
                        >
                          <i class="fas fa-plug text-xs"></i>
                          Reconnect
                        </button>
                      </div>
                    </div>
                    <!-- Account Info Card -->
                    <div
                      class="bg-blue-500/10 rounded-xl p-3 sm:p-4 border border-blue-500/20"
                    >
                      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <div
                          class="h-10 w-10 bg-card rounded-lg flex items-center justify-center shadow-sm shrink-0"
                        >
                          <i
                            class="fas fa-user-circle text-blue-600 dark:text-blue-400 text-xl"
                          ></i>
                        </div>
                        <div class="min-w-0 flex-1">
                          <p class="text-sm font-medium text-foreground break-words">
                            {{ integrations.olj_email }}
                          </p>
                          <p class="text-xs text-muted-foreground">Connected account</p>
                        </div>
                        <div class="text-xs text-muted-foreground sm:text-right sm:shrink-0">
                          <div class="flex items-center sm:justify-end">
                            <i class="fas fa-sync-alt mr-1 text-green-500"></i>
                            <span v-if="integrations.olj_last_sync_at">
                              Last sync: {{ formatLastSync(integrations.olj_last_sync_at) }}
                            </span>
                            <span v-else>
                              Auto-sync enabled
                            </span>
                          </div>
                          <div class="mt-2 flex flex-wrap gap-2 sm:justify-end">
                          <button
                            @click="checkForProfileUpdates"
                            :disabled="checkingForUpdates"
                            class="inline-flex items-center rounded-lg border border-blue-500/30 bg-card px-2.5 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:text-muted-foreground"
                          >
                            <i
                              class="fas fa-refresh mr-1 shrink-0"
                              :class="{ 'fa-spin': checkingForUpdates }"
                            ></i>
                            {{ checkingForUpdates ? "Checking..." : "Check now" }}
                          </button>
                          <button
                            @click="forceSyncProfile"
                            :disabled="forceSyncing"
                            class="inline-flex items-center rounded-lg border border-orange-500/30 bg-card px-2.5 py-1 text-xs font-medium text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 disabled:text-muted-foreground"
                            title="Force sync even if recently synced"
                          >
                            <i
                              class="fas fa-bolt mr-1 shrink-0"
                              :class="{ 'fa-spin': forceSyncing }"
                            ></i>
                            {{ forceSyncing ? "Forcing..." : "Force" }}
                          </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Integration Options -->
                    <div class="space-y-3">
                      <div
                        class="flex items-start justify-between gap-3 p-3 bg-card rounded-xl border border-border shadow-sm"
                      >
                        <div class="flex items-start gap-3 min-w-0">
                          <div
                            class="h-9 w-9 bg-indigo-500/15 rounded-lg flex items-center justify-center"
                          >
                            <i
                              class="fas fa-sync-alt text-indigo-600 dark:text-indigo-400 text-sm"
                            ></i>
                          </div>
                          <div class="min-w-0">
                            <p class="text-sm font-medium text-foreground">
                              Auto-Sync Applications
                            </p>
                            <p class="text-xs text-muted-foreground">
                              Automatically sync your job applications
                            </p>
                          </div>
                        </div>
                        <label
                          class="relative inline-flex items-center cursor-pointer shrink-0 mt-1"
                        >
                          <input
                            v-model="integrations.olj_sync_applications"
                            type="checkbox"
                            class="sr-only peer"
                          />
                          <div
                            class="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"
                          ></div>
                        </label>
                      </div>

                    </div>
                  </div>

                  <!-- Pending Verification State -->
                  <div
                    v-else-if="integrations.olj_pending_verification"
                    class="mt-6 space-y-4"
                  >
                    <!-- Pending Verification Alert -->
                    <div
                      class="bg-amber-500/10 rounded-xl p-5 border border-amber-500/30"
                    >
                      <div class="flex items-start space-x-4">
                        <div
                          class="h-12 w-12 bg-amber-500/15 rounded-xl flex items-center justify-center flex-shrink-0"
                        >
                          <i
                            class="fas fa-envelope-open-text text-amber-600 dark:text-amber-400 text-xl"
                          ></i>
                        </div>
                        <div class="flex-1">
                          <h4 class="font-semibold text-foreground mb-1">
                            Email Verification Required
                          </h4>
                          <p class="text-sm text-muted-foreground mb-3">
                            Your OnlineJobs.ph account has been created with
                            <strong>{{ integrations.olj_pending_email }}</strong
                            >. Please check your email inbox and click the
                            verification link to activate your account.
                          </p>
                          <div
                            class="bg-background/60 rounded-lg p-3 mb-3 text-sm text-muted-foreground"
                          >
                            <div class="flex items-center mb-2">
                              <i
                                class="fas fa-info-circle text-amber-600 dark:text-amber-400 mr-2"
                              ></i>
                              <span class="font-medium">Next Steps:</span>
                            </div>
                            <ol class="list-decimal list-inside space-y-1 ml-1">
                              <li>
                                Check your email inbox (and spam/junk folder)
                              </li>
                              <li>
                                Click the verification link from OnlineJobs.ph
                              </li>
                              <li>
                                Return here and click "Verify & Connect" below
                              </li>
                            </ol>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="flex flex-col sm:flex-row gap-3">
                      <button
                        @click="verifyOljCredentials"
                        class="flex-1 flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-medium shadow-lg shadow-green-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-green-600 disabled:hover:to-emerald-600"
                        :disabled="integrationsSaving"
                      >
                        <span
                          v-if="integrationsSaving"
                          class="flex items-center"
                        >
                          <i class="fas fa-spinner fa-spin mr-2"></i>
                          Verifying...
                        </span>
                        <span v-else class="flex items-center">
                          <i class="fas fa-check-circle mr-2"></i>
                          Verify & Connect
                        </span>
                      </button>
                      <button
                        @click="cancelPendingVerification"
                        class="flex items-center justify-center px-6 py-3 bg-card border border-border text-muted-foreground rounded-xl font-medium hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="integrationsSaving"
                      >
                        <i class="fas fa-times mr-2"></i>
                        Cancel
                      </button>
                    </div>

                    <!-- Help Text -->
                    <p class="text-xs text-muted-foreground text-center">
                      <i class="fas fa-question-circle mr-1"></i>
                      Didn't receive the email? Check your spam folder or cancel
                      and try creating an account again.
                    </p>
                  </div>

                  <!-- Not Connected State - Options -->
                  <div v-else class="mt-6">
                    <p class="text-sm text-muted-foreground mb-4">
                      Connect your OnlineJobs.ph account to enable Auto Apply
                      and application syncing features.
                    </p>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <!-- Connect Existing Account -->
                      <button
                        @click="showOljConnectForm = true"
                        class="group relative overflow-hidden rounded-xl border-2 border-blue-500/20 bg-card p-5 text-left hover:border-blue-500/40 hover:shadow-lg transition-all duration-300"
                      >
                        <div
                          class="absolute top-0 right-0 -mt-2 -mr-2 h-16 w-16 bg-blue-500/10 rounded-full opacity-50 group-hover:scale-150 transition-transform duration-500"
                        ></div>
                        <div class="relative">
                          <div
                            class="h-12 w-12 bg-blue-500/15 rounded-xl flex items-center justify-center mb-3 group-hover:bg-blue-500/25 transition-colors"
                          >
                            <i class="fas fa-link text-blue-600 dark:text-blue-400 text-xl"></i>
                          </div>
                          <h4 class="font-semibold text-foreground mb-1">
                            Connect Existing Account
                          </h4>
                          <p class="text-sm text-muted-foreground">
                            I already have an OnlineJobs.ph account
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- JobStreet Integration Card -->
              <div
                v-if="false"
                class="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-white to-gray-50/50 transition-all duration-300 hover:shadow-lg mt-6"
                :class="{
                  'ring-2 ring-green-500/20': integrations.jobstreet_connected,
                }"
              >
                <div
                  v-if="integrations.jobstreet_connected"
                  class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500"
                ></div>
                <div
                  v-else
                  class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300"
                ></div>

                <div class="p-6">
                  <div class="flex items-start justify-between">
                    <div class="flex items-center space-x-4">
                      <div class="relative">
                        <div
                          class="h-16 w-16 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30"
                        >
                          <i class="fas fa-briefcase text-white text-2xl"></i>
                        </div>
                        <div
                          v-if="integrations.jobstreet_connected"
                          class="absolute -bottom-1 -right-1 h-6 w-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center shadow-sm"
                        >
                          <i class="fas fa-check text-white text-xs"></i>
                        </div>
                      </div>
                      <div>
                        <h3 class="text-lg font-semibold text-foreground">JobStreet</h3>
                        <p class="text-sm text-muted-foreground">JobStreet job applications</p>
                        <div class="flex items-center mt-2 space-x-2">
                          <span
                            :class="[
                              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                              integrations.jobstreet_connected
                                ? 'bg-green-100 text-green-700 border border-green-200'
                                : 'bg-amber-50 text-amber-700 border border-amber-200',
                            ]"
                          >
                            <span
                              v-if="integrations.jobstreet_connected"
                              class="flex items-center"
                            >
                              <span
                                class="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5 animate-pulse"
                              ></span>
                              Connected
                            </span>
                            <span v-else class="flex items-center">
                              <span
                                class="w-1.5 h-1.5 bg-amber-500 rounded-full mr-1.5"
                              ></span>
                              Not Connected
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div v-if="integrations.jobstreet_connected">
                      <button
                        @click="showDisconnectJobStreetModal = true"
                        class="group flex items-center space-x-2 px-4 py-2 bg-white border border-red-200 rounded-xl text-red-600 hover:bg-red-50 hover:border-red-300 transition-all duration-200 shadow-sm"
                      >
                        <i
                          class="fas fa-unlink text-sm group-hover:rotate-12 transition-transform"
                        ></i>
                        <span class="text-sm font-medium">Disconnect</span>
                      </button>
                    </div>
                  </div>

                  <div v-if="integrations.jobstreet_connected" class="mt-6">
                    <div
                      class="bg-gradient-to-r from-blue-50/50 to-indigo-50/30 rounded-xl p-4 border border-blue-100"
                    >
                      <div class="flex items-center space-x-3">
                        <div
                          class="h-10 w-10 bg-white rounded-lg flex items-center justify-center shadow-sm"
                        >
                          <i class="fas fa-user-circle text-blue-500 text-xl"></i>
                        </div>
                        <div>
                          <p class="text-sm font-medium text-foreground">
                            {{ integrations.jobstreet_email }}
                          </p>
                          <p class="text-xs text-muted-foreground">Connected account</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-else class="mt-6">
                    <p class="text-sm text-muted-foreground mb-4">
                      Connect your JobStreet account to enable Auto Apply for JobStreet jobs.
                    </p>
                    <button
                      @click="integrations.jobstreet_connected ? showToastNotification('info', 'Already Connected', 'Your JobStreet account is already connected.') : showJobStreetConnectForm = true"
                      class="group relative overflow-hidden rounded-xl border-2 border-blue-100 bg-white p-5 text-left hover:border-blue-300 hover:shadow-lg transition-all duration-300 w-full"
                    >
                      <div
                        class="absolute top-0 right-0 -mt-2 -mr-2 h-16 w-16 bg-blue-50 rounded-full opacity-50 group-hover:scale-150 transition-transform duration-500"
                      ></div>
                      <div class="relative flex items-center space-x-4">
                        <div
                          class="h-12 w-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors"
                        >
                          <i class="fas fa-link text-blue-600 text-2xl"></i>
                        </div>
                        <div>
                          <h4 class="font-semibold text-foreground mb-1">
                            Connect JobStreet Account
                          </h4>
                          <p class="text-sm text-muted-foreground">
                            Enter your email to receive OTP
                          </p>
                        </div>
                        <i
                          class="fas fa-chevron-right ml-auto text-muted-foreground group-hover:text-blue-500 transition-colors"
                        ></i>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <!-- HIDDEN: LinkedIn -->
              <template v-if="false">
              <!-- LinkedIn: full UI for Basic / Premium only -->
              <div
                v-if="canUseLinkedInIntegration"
                class="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-white to-gray-50/50 transition-all duration-300 hover:shadow-lg mt-6"
                :class="{
                  'ring-2 ring-blue-500/20': integrations.linkedin_connected,
                }"
              >
                <!-- Status Banner -->
                <div
                  v-if="integrations.linkedin_connected"
                  class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500"
                ></div>
                <div
                  v-else
                  class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300"
                ></div>

                <div class="p-6">
                  <!-- Header -->
                  <div class="flex items-start justify-between">
                    <div class="flex items-center space-x-4">
                      <div class="relative">
                        <div
                          class="h-16 w-16 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30"
                        >
                          <i class="fab fa-linkedin text-white text-3xl"></i>
                        </div>
                        <div
                          v-if="integrations.linkedin_connected"
                          class="absolute -bottom-1 -right-1 h-6 w-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center shadow-sm"
                        >
                          <i class="fas fa-check text-white text-xs"></i>
                        </div>
                      </div>
                      <div>
                        <h3 class="text-lg font-semibold text-foreground">
                          LinkedIn
                        </h3>
                        <p class="text-sm text-muted-foreground">
                          World's largest professional network
                        </p>
                        <div class="flex items-center mt-2 space-x-2">
                          <span
                            :class="[
                              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                              integrations.linkedin_connected
                                ? 'bg-green-100 text-green-700 border border-green-200'
                                : 'bg-amber-50 text-amber-700 border border-amber-200',
                            ]"
                          >
                            <span
                              v-if="integrations.linkedin_connected"
                              class="flex items-center"
                            >
                              <span
                                class="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5 animate-pulse"
                              ></span>
                              Connected
                            </span>
                            <span v-else class="flex items-center">
                              <span
                                class="w-1.5 h-1.5 bg-amber-500 rounded-full mr-1.5"
                              ></span>
                              Not Connected
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Action Button -->
                    <div v-if="integrations.linkedin_connected">
                      <button
                        @click="showDisconnectLinkedInModal = true"
                        class="group flex items-center space-x-2 px-4 py-2 bg-white border border-red-200 rounded-xl text-red-600 hover:bg-red-50 hover:border-red-300 transition-all duration-200 shadow-sm"
                      >
                        <i
                          class="fas fa-unlink text-sm group-hover:rotate-12 transition-transform"
                        ></i>
                        <span class="text-sm font-medium">Disconnect</span>
                      </button>
                    </div>
                  </div>

                  <!-- Connected State -->
                  <div
                    v-if="integrations.linkedin_connected"
                    class="mt-6 space-y-4"
                  >
                    <!-- Account Info Card -->
                    <div
                      class="bg-gradient-to-r from-blue-50/50 to-indigo-50/30 rounded-xl p-4 border border-blue-100"
                    >
                      <div class="flex items-center space-x-3">
                        <div
                          class="h-10 w-10 bg-white rounded-lg flex items-center justify-center shadow-sm"
                        >
                          <i
                            class="fas fa-user-circle text-blue-500 text-xl"
                          ></i>
                        </div>
                        <div>
                          <p class="text-sm font-medium text-foreground">
                            {{ integrations.linkedin_email }}
                          </p>
                          <p class="text-xs text-muted-foreground">Connected account</p>
                        </div>
                        <button
                          @click="openUpdateLinkedInForm"
                          class="ml-auto px-3 py-1.5 bg-white border border-border rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:border-border transition-colors"
                        >
                          Update Credentials
                        </button>
                      </div>
                    </div>

                    <!-- Integration Options -->
                    <div class="space-y-3">
                      <div
                        class="flex items-center justify-between p-3 bg-white rounded-xl border border-border shadow-sm"
                      >
                        <div class="flex items-center space-x-3">
                          <div
                            class="h-9 w-9 bg-indigo-50 rounded-lg flex items-center justify-center"
                          >
                            <i
                              class="fas fa-sync-alt text-indigo-500 text-sm"
                            ></i>
                          </div>
                          <div>
                            <p class="text-sm font-medium text-foreground">
                              Auto-Sync Profile
                            </p>
                            <p class="text-xs text-muted-foreground">
                              Sync your LinkedIn profile to Applypa
                            </p>
                          </div>
                        </div>
                        <label
                          class="relative inline-flex items-center cursor-pointer"
                        >
                          <input
                            v-model="integrations.linkedin_sync_profile"
                            type="checkbox"
                            class="sr-only peer"
                          />
                          <div
                            class="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"
                          ></div>
                        </label>
                      </div>

                      <div
                        class="flex items-center justify-between p-3 bg-white rounded-xl border border-border shadow-sm"
                      >
                        <div class="flex items-center space-x-3">
                          <div
                            class="h-9 w-9 bg-blue-50 rounded-lg flex items-center justify-center"
                          >
                            <i
                              class="fas fa-briefcase text-blue-500 text-sm"
                            ></i>
                          </div>
                          <div>
                            <p class="text-sm font-medium text-foreground">
                              Import Job Applications
                            </p>
                            <p class="text-xs text-muted-foreground">
                              Import jobs you've applied to on LinkedIn
                            </p>
                          </div>
                        </div>
                        <label
                          class="relative inline-flex items-center cursor-pointer"
                        >
                          <input
                            v-model="integrations.linkedin_sync_applications"
                            type="checkbox"
                            class="sr-only peer"
                          />
                          <div
                            class="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"
                          ></div>
                        </label>
                      </div>
                    </div>

                  </div>

                  <!-- Not Connected State -->
                  <div v-else class="mt-6">
                    <p class="text-sm text-muted-foreground mb-4">
                      Connect your LinkedIn account to sync your profile and
                      import job applications.
                    </p>

                    <button
                      @click="showLinkedInConnectForm = true"
                      class="group relative overflow-hidden rounded-xl border-2 border-blue-100 bg-white p-5 text-left hover:border-blue-300 hover:shadow-lg transition-all duration-300 w-full"
                    >
                      <div
                        class="absolute top-0 right-0 -mt-2 -mr-2 h-16 w-16 bg-blue-50 rounded-full opacity-50 group-hover:scale-150 transition-transform duration-500"
                      ></div>
                      <div class="relative flex items-center space-x-4">
                        <div
                          class="h-12 w-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors"
                        >
                          <i class="fab fa-linkedin text-blue-600 text-2xl"></i>
                        </div>
                        <div>
                          <h4 class="font-semibold text-foreground mb-1">
                            Connect LinkedIn Account
                          </h4>
                          <p class="text-sm text-muted-foreground">
                            Sign in with your LinkedIn credentials
                          </p>
                        </div>
                        <i
                          class="fas fa-chevron-right ml-auto text-muted-foreground group-hover:text-blue-500 transition-colors"
                        ></i>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Free plan: still connected (e.g. after downgrade) — disconnect only -->
              <div
                v-else-if="integrations.linkedin_connected"
                class="relative overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-sm mt-6 p-6"
              >
                <div class="flex items-start gap-4">
                  <div
                    class="h-14 w-14 shrink-0 bg-blue-100 rounded-2xl flex items-center justify-center"
                  >
                    <i class="fab fa-linkedin text-blue-600 text-2xl"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-lg font-semibold text-foreground">
                      LinkedIn not on Free plan
                    </h3>
                    <p class="text-sm text-muted-foreground mt-1">
                      Your account is still linked as
                      <strong>{{ integrations.linkedin_email }}</strong>.
                      Upgrade to Basic or Premium to use LinkedIn sync again, or
                      disconnect.
                    </p>
                    <div class="flex flex-wrap gap-3 mt-4">
                      <router-link
                        to="/subscription/plans"
                        class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark text-primary-foreground rounded-xl text-sm font-medium transition-colors"
                      >
                        <i class="fas fa-arrow-up"></i>
                        View subscription plans
                      </router-link>
                      <button
                        type="button"
                        @click="showDisconnectLinkedInModal = true"
                        class="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-red-200 text-red-600 rounded-xl text-sm font-medium hover:bg-red-50 transition-colors"
                      >
                        <i class="fas fa-unlink"></i>
                        Disconnect LinkedIn
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              </template>

              <!-- Free plan: upgrade to use LinkedIn -->
              <!-- <div
                v-else
                class="relative overflow-hidden rounded-2xl border border-sky-200 bg-white shadow-sm mt-6"
              >
                <div
                  class="bg-gradient-to-r from-sky-600 to-blue-700 px-6 py-4"
                >
                  <div class="flex items-center gap-3">
                    <div
                      class="h-10 w-10 bg-white/20 rounded-xl flex items-center justify-center"
                    >
                      <i class="fab fa-linkedin text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 class="text-lg font-semibold text-white">
                        LinkedIn integration
                      </h3>
                      <p class="text-sky-100 text-sm">
                        Included with Basic and Premium
                      </p>
                    </div>
                  </div>
                </div>
                <div class="p-6 space-y-4">
                  <p class="text-muted-foreground text-sm leading-relaxed">
                    Connect LinkedIn to sync your profile to Applypa and import
                    job applications. This is not available on the Free plan.
                  </p>
                  <router-link
                    to="/subscription/plans"
                    class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark text-primary-foreground rounded-xl font-medium transition-colors"
                  >
                    <i class="fas fa-arrow-up"></i>
                    View subscription plans
                  </router-link>
                </div>
              </div> -->
              <!-- /HIDDEN: LinkedIn -->

              <!-- Other Integrations Coming Soon -->
              <div class="my-6">
                <div
                  class="rounded-xl border border-dashed border-border bg-muted/50 p-6 text-center"
                >
                  <div
                    class="inline-flex items-center justify-center h-12 w-12 rounded-full bg-muted mb-3"
                  >
                    <i class="fas fa-plus text-muted-foreground"></i>
                  </div>
                  <p class="text-sm font-medium text-muted-foreground">
                    More integrations coming soon
                  </p>
                  <p class="text-xs text-muted-foreground mt-1">
                    We're working on connecting more job platforms
                  </p>
                </div>
              </div>

            </div>
          </div>

          <!-- Privacy Section (removed) -->
          <div v-if="false" class="space-y-6">
            <!-- Profile Visibility Card -->
            <div
              class="bg-white rounded-2xl shadow-lg border border-border overflow-hidden"
            >
              <div
                class="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-3">
                    <div
                      class="h-10 w-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
                    >
                      <i class="fas fa-eye text-white text-lg"></i>
                    </div>
                    <div>
                      <h3 class="text-lg font-semibold text-white">
                        Profile Visibility
                      </h3>
                      <p class="text-purple-100 text-sm">
                        Control who can see your profile
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="p-6 space-y-4">
                <!-- Public Option -->
                <label
                  class="flex items-start p-4 rounded-xl border cursor-pointer transition-all hover:bg-muted"
                  :class="
                    privacySettings.profile_visibility === 'public'
                      ? 'border-purple-500 bg-purple-50/50'
                      : 'border-border'
                  "
                >
                  <div class="flex items-center h-5 mt-0.5">
                    <input
                      v-model="privacySettings.profile_visibility"
                      value="public"
                      type="radio"
                      class="h-5 w-5 text-purple-600 focus:ring-purple-500 border-border"
                    />
                  </div>
                  <div class="ml-4 flex-1">
                    <div class="flex items-center">
                      <span
                        class="font-semibold text-foreground"
                        :class="
                          privacySettings.profile_visibility === 'public'
                            ? 'text-purple-700'
                            : ''
                        "
                        >Public</span
                      >
                      <span
                        v-if="privacySettings.profile_visibility === 'public'"
                        class="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full"
                        >Active</span
                      >
                    </div>
                    <p class="text-sm text-muted-foreground mt-1">
                      Your profile is visible to all employers and recruiters on
                      the platform
                    </p>
                  </div>
                  <div
                    class="ml-4 h-10 w-10 rounded-full flex items-center justify-center"
                    :class="
                      privacySettings.profile_visibility === 'public'
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-muted text-muted-foreground'
                    "
                  >
                    <i class="fas fa-globe"></i>
                  </div>
                </label>

                <!-- Limited Option -->
                <label
                  class="flex items-start p-4 rounded-xl border cursor-pointer transition-all hover:bg-muted"
                  :class="
                    privacySettings.profile_visibility === 'limited'
                      ? 'border-purple-500 bg-purple-50/50'
                      : 'border-border'
                  "
                >
                  <div class="flex items-center h-5 mt-0.5">
                    <input
                      v-model="privacySettings.profile_visibility"
                      value="limited"
                      type="radio"
                      class="h-5 w-5 text-purple-600 focus:ring-purple-500 border-border"
                    />
                  </div>
                  <div class="ml-4 flex-1">
                    <div class="flex items-center">
                      <span
                        class="font-semibold text-foreground"
                        :class="
                          privacySettings.profile_visibility === 'limited'
                            ? 'text-purple-700'
                            : ''
                        "
                        >Limited</span
                      >
                      <span
                        v-if="privacySettings.profile_visibility === 'limited'"
                        class="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full"
                        >Active</span
                      >
                    </div>
                    <p class="text-sm text-muted-foreground mt-1">
                      Only employers you apply to can view your full profile
                    </p>
                  </div>
                  <div
                    class="ml-4 h-10 w-10 rounded-full flex items-center justify-center"
                    :class="
                      privacySettings.profile_visibility === 'limited'
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-muted text-muted-foreground'
                    "
                  >
                    <i class="fas fa-user-check"></i>
                  </div>
                </label>

                <!-- Private Option -->
                <label
                  class="flex items-start p-4 rounded-xl border cursor-pointer transition-all hover:bg-muted"
                  :class="
                    privacySettings.profile_visibility === 'private'
                      ? 'border-purple-500 bg-purple-50/50'
                      : 'border-border'
                  "
                >
                  <div class="flex items-center h-5 mt-0.5">
                    <input
                      v-model="privacySettings.profile_visibility"
                      value="private"
                      type="radio"
                      class="h-5 w-5 text-purple-600 focus:ring-purple-500 border-border"
                    />
                  </div>
                  <div class="ml-4 flex-1">
                    <div class="flex items-center">
                      <span
                        class="font-semibold text-foreground"
                        :class="
                          privacySettings.profile_visibility === 'private'
                            ? 'text-purple-700'
                            : ''
                        "
                        >Private</span
                      >
                      <span
                        v-if="privacySettings.profile_visibility === 'private'"
                        class="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full"
                        >Active</span
                      >
                    </div>
                    <p class="text-sm text-muted-foreground mt-1">
                      Your profile is hidden from search results and employer
                      browsing
                    </p>
                  </div>
                  <div
                    class="ml-4 h-10 w-10 rounded-full flex items-center justify-center"
                    :class="
                      privacySettings.profile_visibility === 'private'
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-muted text-muted-foreground'
                    "
                  >
                    <i class="fas fa-lock"></i>
                  </div>
                </label>
              </div>
            </div>

            <!-- Contact Preferences Card -->
            <div
              class="bg-white rounded-2xl shadow-lg border border-border overflow-hidden"
            >
              <div class="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4">
                <div class="flex items-center space-x-3">
                  <div
                    class="h-10 w-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
                  >
                    <i class="fas fa-address-card text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-white">
                      Contact Preferences
                    </h3>
                    <p class="text-blue-100 text-sm">
                      Manage how recruiters can reach you
                    </p>
                  </div>
                </div>
              </div>

              <div class="p-6 space-y-4">
                <!-- Allow Recruiter Contact -->
                <label
                  class="flex items-center p-4 rounded-xl border cursor-pointer transition-all hover:bg-muted"
                  :class="
                    privacySettings.allow_recruiter_contact
                      ? 'border-blue-500 bg-blue-50/50'
                      : 'border-border'
                  "
                >
                  <div
                    class="h-12 w-12 rounded-xl flex items-center justify-center mr-4"
                    :class="
                      privacySettings.allow_recruiter_contact
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-muted text-muted-foreground'
                    "
                  >
                    <i class="fas fa-comments text-xl"></i>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center">
                      <span class="font-semibold text-foreground"
                        >Allow Recruiter Contact</span
                      >
                      <span
                        v-if="privacySettings.allow_recruiter_contact"
                        class="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full"
                        >Enabled</span
                      >
                    </div>
                    <p class="text-sm text-muted-foreground mt-0.5">
                      Recruiters can send you messages and job opportunities
                    </p>
                  </div>
                  <div class="ml-4">
                    <div
                      class="relative inline-flex h-7 w-12 items-center rounded-full transition-colors"
                      :class="
                        privacySettings.allow_recruiter_contact
                          ? 'bg-blue-600'
                          : 'bg-muted'
                      "
                    >
                      <input
                        v-model="privacySettings.allow_recruiter_contact"
                        type="checkbox"
                        class="sr-only"
                      />
                      <span
                        class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform"
                        :class="
                          privacySettings.allow_recruiter_contact
                            ? 'translate-x-6'
                            : 'translate-x-1'
                        "
                      ></span>
                    </div>
                  </div>
                </label>

                <!-- Hide From Current Employer -->
                <label
                  class="flex items-center p-4 rounded-xl border cursor-pointer transition-all hover:bg-muted"
                  :class="
                    privacySettings.hide_current_employer
                      ? 'border-blue-500 bg-blue-50/50'
                      : 'border-border'
                  "
                >
                  <div
                    class="h-12 w-12 rounded-xl flex items-center justify-center mr-4"
                    :class="
                      privacySettings.hide_current_employer
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-muted text-muted-foreground'
                    "
                  >
                    <i class="fas fa-user-secret text-xl"></i>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center">
                      <span class="font-semibold text-foreground"
                        >Hide from Current Employer</span
                      >
                      <span
                        v-if="privacySettings.hide_current_employer"
                        class="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full"
                        >Enabled</span
                      >
                    </div>
                    <p class="text-sm text-muted-foreground mt-0.5">
                      Prevent your current employer from seeing your job search
                      activity
                    </p>
                  </div>
                  <div class="ml-4">
                    <div
                      class="relative inline-flex h-7 w-12 items-center rounded-full transition-colors"
                      :class="
                        privacySettings.hide_current_employer
                          ? 'bg-blue-600'
                          : 'bg-muted'
                      "
                    >
                      <input
                        v-model="privacySettings.hide_current_employer"
                        type="checkbox"
                        class="sr-only"
                      />
                      <span
                        class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform"
                        :class="
                          privacySettings.hide_current_employer
                            ? 'translate-x-6'
                            : 'translate-x-1'
                        "
                      ></span>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <!-- Data Management Card -->
            <div
              class="bg-white rounded-2xl shadow-lg border border-border overflow-hidden"
            >
              <div
                class="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4"
              >
                <div class="flex items-center space-x-3">
                  <div
                    class="h-10 w-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
                  >
                    <i class="fas fa-database text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-white">
                      Data Management
                    </h3>
                    <p class="text-amber-100 text-sm">
                      Control your personal data
                    </p>
                  </div>
                </div>
              </div>

              <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Export Data Button -->
                  <button
                    @click="requestDataExport"
                    :disabled="dataExporting"
                    class="group flex items-center p-4 rounded-xl border hover:border-green-500 hover:bg-green-50/50 transition-all text-left"
                  >
                    <div
                      class="h-12 w-12 bg-green-100 group-hover:bg-green-200 rounded-xl flex items-center justify-center mr-4 transition-colors"
                    >
                      <i
                        class="fas fa-file-export text-green-600 text-xl"
                        :class="dataExporting ? 'fa-spinner fa-spin' : ''"
                      ></i>
                    </div>
                    <div class="flex-1">
                      <h4 class="font-semibold text-foreground">
                        Export Your Data
                      </h4>
                      <p class="text-sm text-muted-foreground">
                        {{
                          dataExporting
                            ? "Preparing..."
                            : "Download all your data"
                        }}
                      </p>
                    </div>
                    <i
                      class="fas fa-chevron-right text-muted-foreground group-hover:text-green-500 transition-colors"
                    ></i>
                  </button>

                </div>

                <div
                  class="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-100"
                >
                  <div class="flex items-start">
                    <i
                      class="fas fa-info-circle text-amber-500 mt-0.5 mr-3"
                    ></i>
                    <p class="text-sm text-amber-700">
                      <strong>Data Privacy Notice:</strong> Your data is stored
                      securely and never shared with third parties without your
                      consent. You can export or delete your data at any time.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Save Button -->
            <div class="flex justify-end">
              <button
                @click="savePrivacySettings"
                class="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-medium shadow-lg shadow-purple-500/30 transition-all flex items-center space-x-2"
                :disabled="privacySaving"
              >
                <span v-if="privacySaving">
                  <i class="fas fa-spinner fa-spin mr-2"></i>
                  Saving Changes...
                </span>
                <span v-else class="flex items-center">
                  <i class="fas fa-save mr-2"></i>
                  Save Privacy Settings
                </span>
              </button>
            </div>
          </div>

          <!-- Admin Tools Section -->
          <div v-if="activeSection === 'admin' && !loading" class="space-y-6">
            <!-- Admin Tools Header -->
            <div
              class="bg-card rounded-2xl shadow-lg border border-border overflow-hidden"
            >
              <div
                class="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4"
              >
                <div class="flex items-center space-x-3">
                  <div
                    class="h-10 w-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
                  >
                    <i class="fas fa-tools text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-white">
                      Admin Tools
                    </h3>
                    <p class="text-orange-100 text-sm">
                      Administrative functions for platform management
                    </p>
                  </div>
                </div>
              </div>

              <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Scrape Jobs Button -->
                  <button
                    @click="runScrapeJobs"
                    :disabled="adminToolsLoading.scrapeJobs || isScrapeLocked"
                    class="group flex items-center p-4 rounded-xl border border-border hover:border-blue-500 hover:bg-blue-500/10 transition-all text-left relative"
                    :class="{
                      'border-blue-400 bg-blue-500/10':
                        adminToolsStatus.scrapeJobs.status === 'running',
                      'border-red-400 bg-red-500/10':
                        adminToolsStatus.scrapeJobs.status === 'error',
                    }"
                  >
                    <!-- Subtle Status Indicator -->
                    <div
                      v-if="adminToolsStatus.scrapeJobs.status !== 'idle'"
                      class="absolute top-2 right-2 flex items-center"
                    >
                      <span
                        v-if="adminToolsStatus.scrapeJobs.status === 'running'"
                        class="flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/15 text-blue-700 dark:text-blue-300"
                      >
                        <i class="fas fa-spinner fa-spin mr-1"></i>
                        {{ adminToolsStatus.scrapeJobs.message }}
                      </span>
                      <span
                        v-else-if="
                          adminToolsStatus.scrapeJobs.status === 'success'
                        "
                        class="flex items-center text-green-600 dark:text-green-400"
                        title="Completed"
                      >
                        <i class="fas fa-check-circle text-sm"></i>
                      </span>
                      <span
                        v-else-if="
                          adminToolsStatus.scrapeJobs.status === 'error'
                        "
                        class="flex items-center text-red-600 dark:text-red-400"
                        title="Error"
                      >
                        <i class="fas fa-exclamation-circle text-sm"></i>
                      </span>
                    </div>
                    <div
                      class="h-12 w-12 bg-blue-500/15 group-hover:bg-blue-500/25 rounded-xl flex items-center justify-center mr-4 transition-colors"
                    >
                      <i
                        class="fas fa-search text-blue-600 dark:text-blue-400 text-xl"
                        :class="{
                          'fa-spinner fa-spin': adminToolsLoading.scrapeJobs,
                        }"
                      ></i>
                    </div>
                    <div class="flex-1">
                      <h4 class="font-semibold text-foreground">Scrape Jobs</h4>
                      <p class="text-sm text-muted-foreground">
                        {{
                          adminToolsLoading.scrapeJobs
                            ? "Scraping in progress..."
                            : "Start scraping job posts from the external platform"
                        }}
                      </p>
                    </div>
                    <i
                      class="fas fa-chevron-right text-muted-foreground group-hover:text-blue-500 transition-colors"
                    ></i>
                  </button>

                  <!-- Scrape Job Hunters Button -->
                  <button
                    @click="runScrapeJobHunters"
                    :disabled="adminToolsLoading.scrapeJobHunters"
                    class="group flex items-center p-4 rounded-xl border border-border hover:border-green-500 hover:bg-green-500/10 transition-all text-left relative"
                    :class="{
                      'border-green-400 bg-green-500/10':
                        adminToolsStatus.scrapeJobHunters.status === 'running',
                      'border-red-400 bg-red-500/10':
                        adminToolsStatus.scrapeJobHunters.status === 'error',
                    }"
                  >
                    <!-- Subtle Status Indicator -->
                    <div
                      v-if="adminToolsStatus.scrapeJobHunters.status !== 'idle'"
                      class="absolute top-2 right-2 flex items-center"
                    >
                      <span
                        v-if="
                          adminToolsStatus.scrapeJobHunters.status === 'running'
                        "
                        class="flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/15 text-green-700 dark:text-green-300"
                      >
                        <i class="fas fa-spinner fa-spin mr-1"></i>
                        {{ adminToolsStatus.scrapeJobHunters.message }}
                      </span>
                      <span
                        v-else-if="
                          adminToolsStatus.scrapeJobHunters.status === 'success'
                        "
                        class="flex items-center text-green-600 dark:text-green-400"
                        title="Completed"
                      >
                        <i class="fas fa-check-circle text-sm"></i>
                      </span>
                      <span
                        v-else-if="
                          adminToolsStatus.scrapeJobHunters.status === 'error'
                        "
                        class="flex items-center text-red-600 dark:text-red-400"
                        title="Error"
                      >
                        <i class="fas fa-exclamation-circle text-sm"></i>
                      </span>
                    </div>
                    <div
                      class="h-12 w-12 bg-green-500/15 group-hover:bg-green-500/25 rounded-xl flex items-center justify-center mr-4 transition-colors"
                    >
                      <i
                        class="fas fa-user-friends text-green-600 dark:text-green-400 text-xl"
                        :class="{
                          'fa-spinner fa-spin':
                            adminToolsLoading.scrapeJobHunters,
                        }"
                      ></i>
                    </div>
                    <div class="flex-1">
                      <h4 class="font-semibold text-foreground">
                        Scrape Job Hunters
                      </h4>
                      <p class="text-sm text-muted-foreground">
                        {{
                          adminToolsLoading.scrapeJobHunters
                            ? "Scraping in progress..."
                            : "Start scraping job hunters from the external platform"
                        }}
                      </p>
                    </div>
                    <i
                      class="fas fa-chevron-right text-muted-foreground group-hover:text-green-500 transition-colors"
                    ></i>
                  </button>

                  <!-- Sync Applications Button -->
                  <button
                    @click="runSyncApplications"
                    :disabled="adminToolsLoading.syncApplications"
                    class="group flex items-center p-4 rounded-xl border border-border hover:border-purple-500 hover:bg-purple-500/10 transition-all text-left relative"
                    :class="{
                      'border-purple-400 bg-purple-500/10':
                        adminToolsStatus.syncApplications.status === 'running',
                      'border-red-400 bg-red-500/10':
                        adminToolsStatus.syncApplications.status === 'error',
                    }"
                  >
                    <!-- Subtle Status Indicator -->
                    <div
                      v-if="adminToolsStatus.syncApplications.status !== 'idle'"
                      class="absolute top-2 right-2 flex items-center"
                    >
                      <span
                        v-if="
                          adminToolsStatus.syncApplications.status === 'running'
                        "
                        class="flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-purple-500/15 text-purple-700 dark:text-purple-300"
                      >
                        <i class="fas fa-spinner fa-spin mr-1"></i>
                        {{ adminToolsStatus.syncApplications.message }}
                      </span>
                      <span
                        v-else-if="
                          adminToolsStatus.syncApplications.status === 'success'
                        "
                        class="flex items-center text-green-600 dark:text-green-400"
                        title="Completed"
                      >
                        <i class="fas fa-check-circle text-sm"></i>
                      </span>
                      <span
                        v-else-if="
                          adminToolsStatus.syncApplications.status === 'error'
                        "
                        class="flex items-center text-red-600 dark:text-red-400"
                        title="Error"
                      >
                        <i class="fas fa-exclamation-circle text-sm"></i>
                      </span>
                    </div>
                    <div
                      class="h-12 w-12 bg-purple-500/15 group-hover:bg-purple-500/25 rounded-xl flex items-center justify-center mr-4 transition-colors"
                    >
                      <i
                        class="fas fa-sync text-purple-600 dark:text-purple-400 text-xl"
                        :class="{
                          'fa-spinner fa-spin':
                            adminToolsLoading.syncApplications,
                        }"
                      ></i>
                    </div>
                    <div class="flex-1">
                      <h4 class="font-semibold text-foreground">
                        Sync Applications
                      </h4>
                      <p class="text-sm text-muted-foreground">
                        {{
                          adminToolsLoading.syncApplications
                            ? "Syncing..."
                            : "Sync job applications from the external platform"
                        }}
                      </p>
                    </div>
                    <i
                      class="fas fa-chevron-right text-muted-foreground group-hover:text-purple-500 transition-colors"
                    ></i>
                  </button>

                  <!-- Refresh Employers Button -->
                  <button
                    @click="runRefreshEmployers"
                    :disabled="adminToolsLoading.refreshEmployers"
                    class="group flex items-center p-4 rounded-xl border border-border hover:border-emerald-500 hover:bg-emerald-500/10 transition-all text-left relative"
                    :class="{
                      'border-emerald-400 bg-emerald-500/10':
                        adminToolsStatus.refreshEmployers.status === 'running',
                      'border-red-400 bg-red-500/10':
                        adminToolsStatus.refreshEmployers.status === 'error',
                    }"
                  >
                    <!-- Subtle Status Indicator -->
                    <div
                      v-if="adminToolsStatus.refreshEmployers.status !== 'idle'"
                      class="absolute top-2 right-2 flex items-center"
                    >
                      <span
                        v-if="
                          adminToolsStatus.refreshEmployers.status === 'running'
                        "
                        class="flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                      >
                        <i class="fas fa-spinner fa-spin mr-1"></i>
                        {{ adminToolsStatus.refreshEmployers.message }}
                      </span>
                      <span
                        v-else-if="
                          adminToolsStatus.refreshEmployers.status === 'success'
                        "
                        class="flex items-center text-green-600 dark:text-green-400"
                        title="Completed"
                      >
                        <i class="fas fa-check-circle text-sm"></i>
                      </span>
                      <span
                        v-else-if="
                          adminToolsStatus.refreshEmployers.status === 'error'
                        "
                        class="flex items-center text-red-600 dark:text-red-400"
                        title="Error"
                      >
                        <i class="fas fa-exclamation-circle text-sm"></i>
                      </span>
                    </div>
                    <div
                      class="h-12 w-12 bg-emerald-500/15 group-hover:bg-emerald-500/25 rounded-xl flex items-center justify-center mr-4 transition-colors"
                    >
                      <i
                        class="fas fa-sync-alt text-emerald-600 dark:text-emerald-400 text-xl"
                        :class="{
                          'fa-spinner fa-spin':
                            adminToolsLoading.refreshEmployers,
                        }"
                      ></i>
                    </div>
                    <div class="flex-1">
                      <h4 class="font-semibold text-foreground">
                        Refresh Employers
                      </h4>
                      <p class="text-sm text-muted-foreground">
                        {{
                          adminToolsLoading.refreshEmployers
                            ? "Refreshing employer stats..."
                            : "Update employer job counts & metadata"
                        }}
                      </p>
                    </div>
                    <i
                      class="fas fa-chevron-right text-muted-foreground group-hover:text-emerald-500 transition-colors"
                    ></i>
                  </button>

                  <!-- Cleanup Jobs Button -->
                  <button
                    @click="runCleanupJobs"
                    :disabled="adminToolsLoading.cleanupJobs"
                    class="group flex items-center p-4 rounded-xl border border-border hover:border-amber-500 hover:bg-amber-500/10 transition-all text-left relative"
                    :class="{
                      'border-amber-400 bg-amber-500/10':
                        adminToolsStatus.cleanupJobs.status === 'running',
                      'border-red-400 bg-red-500/10':
                        adminToolsStatus.cleanupJobs.status === 'error',
                    }"
                  >
                    <!-- Subtle Status Indicator -->
                    <div
                      v-if="adminToolsStatus.cleanupJobs.status !== 'idle'"
                      class="absolute top-2 right-2 flex items-center"
                    >
                      <span
                        v-if="adminToolsStatus.cleanupJobs.status === 'running'"
                        class="flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300"
                      >
                        <i class="fas fa-spinner fa-spin mr-1"></i>
                        {{ adminToolsStatus.cleanupJobs.message }}
                      </span>
                      <span
                        v-else-if="
                          adminToolsStatus.cleanupJobs.status === 'success'
                        "
                        class="flex items-center text-green-600 dark:text-green-400"
                        title="Completed"
                      >
                        <i class="fas fa-check-circle text-sm"></i>
                      </span>
                      <span
                        v-else-if="
                          adminToolsStatus.cleanupJobs.status === 'error'
                        "
                        class="flex items-center text-red-600 dark:text-red-400"
                        title="Error"
                      >
                        <i class="fas fa-exclamation-circle text-sm"></i>
                      </span>
                    </div>
                    <div
                      class="h-12 w-12 bg-amber-500/15 group-hover:bg-amber-500/25 rounded-xl flex items-center justify-center mr-4 transition-colors"
                    >
                      <i
                        class="fas fa-broom text-amber-600 dark:text-amber-400 text-xl"
                        :class="{
                          'fa-spinner fa-spin': adminToolsLoading.cleanupJobs,
                        }"
                      ></i>
                    </div>
                    <div class="flex-1">
                      <h4 class="font-semibold text-foreground">Cleanup Jobs</h4>
                      <p class="text-sm text-muted-foreground">
                        {{
                          adminToolsLoading.cleanupJobs
                            ? "Cleaning up..."
                            : "Remove duplicates & fix data issues"
                        }}
                      </p>
                    </div>
                    <i
                      class="fas fa-chevron-right text-muted-foreground group-hover:text-amber-500 transition-colors"
                    ></i>
                  </button>
                </div>

                <!-- Admin Tools Logs -->
                <div v-if="adminToolsLogs.length > 0" class="mt-6">
                  <div class="flex items-center justify-between mb-3">
                    <h4 class="font-semibold text-foreground">Activity Logs</h4>
                    <button
                      @click="clearAdminLogs"
                      class="text-sm text-muted-foreground hover:text-red-500 transition-colors"
                    >
                      <i class="fas fa-trash-alt mr-1"></i>
                      Clear Logs
                    </button>
                  </div>
                  <div class="space-y-2 max-h-64 overflow-y-auto">
                    <div
                      v-for="(log, index) in adminToolsLogs"
                      :key="index"
                      class="p-3 rounded-lg border"
                      :class="
                        log.status === 'success'
                          ? 'bg-green-500/10 border-green-500/30'
                          : 'bg-red-500/10 border-red-500/30'
                      "
                    >
                      <div class="flex items-start">
                        <div class="flex-shrink-0">
                          <i
                            :class="
                              log.status === 'success'
                                ? 'fas fa-check-circle text-green-600 dark:text-green-400'
                                : 'fas fa-exclamation-circle text-red-600 dark:text-red-400'
                            "
                          ></i>
                        </div>
                        <div class="ml-3 flex-1">
                          <p class="text-sm font-medium text-foreground">
                            {{ log.action }}
                          </p>
                          <p class="text-sm text-muted-foreground">{{ log.message }}</p>
                          <p class="text-xs text-muted-foreground mt-1">
                            {{ log.timestamp }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-else
                  class="mt-6 p-4 bg-muted rounded-xl border border-border text-center"
                >
                  <i
                    class="fas fa-clipboard-list text-muted-foreground text-2xl mb-2"
                  ></i>
                  <p class="text-sm text-muted-foreground">
                    No admin activity yet. Run a tool to see the results here.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Subscription Section -->
          <div v-if="activeSection === 'subscription' && !loading" class="flex flex-col gap-5">

            <!-- Loading skeleton -->
            <div v-if="subLoading" class="bg-card rounded-xl border border-border shadow-sm overflow-hidden p-6">
              <div class="flex items-start gap-4">
                <div class="h-14 w-14 rounded-2xl bg-muted animate-pulse shrink-0"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-5 w-44 rounded bg-muted animate-pulse"></div>
                  <div class="h-4 w-32 rounded bg-muted animate-pulse"></div>
                </div>
              </div>
            </div>

            <!-- Error state -->
            <div v-else-if="subError" class="bg-red-500/10 border border-red-500/30 rounded-xl p-5 text-sm text-red-700 dark:text-red-300">
              <i class="fas fa-exclamation-circle mr-2"></i>{{ subError }}
            </div>

            <template v-else-if="subData">
              <!-- ── PLAN CARD ── -->
              <section class="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
                <!-- Plan summary row -->
                <div class="p-5 sm:p-6 flex items-start gap-4">
                  <div :class="['h-14 w-14 shrink-0 rounded-2xl bg-gradient-to-br grid place-items-center shadow-lg', subPlanMeta[subData.plan?.plan_type]?.gradient ?? 'from-gray-400 to-gray-500', subPlanMeta[subData.plan?.plan_type]?.shadow ?? '']">
                    <i :class="[(subPlanMeta[subData.plan?.plan_type]?.icon ?? 'fas fa-circle'), 'text-white text-xl']"></i>
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-2.5 flex-wrap">
                      <h2 class="text-lg sm:text-xl font-extrabold text-foreground leading-tight">{{ subData.status === 'trial' ? 'Free Trial' : (subData.plan?.name ?? 'Free') + ' Plan' }}</h2>
                      <span v-if="subData.status === 'active'" class="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-2.5 py-0.5 text-[11px] font-bold text-emerald-700 dark:text-emerald-300">
                        <span class="h-1.5 w-1.5 rounded-full bg-emerald-500"></span> Active
                      </span>
                      <span v-else-if="subData.status === 'expired'" class="inline-flex items-center gap-1.5 rounded-full bg-red-500/15 border border-red-500/30 px-2.5 py-0.5 text-[11px] font-bold text-red-700 dark:text-red-300">
                        <span class="h-1.5 w-1.5 rounded-full bg-red-500"></span> Expired
                      </span>
                      <span v-else-if="subData.status === 'trial'" class="inline-flex items-center gap-1.5 rounded-full bg-blue-500/15 border border-blue-500/30 px-2.5 py-0.5 text-[11px] font-bold text-blue-700 dark:text-blue-300">
                        <span class="h-1.5 w-1.5 rounded-full bg-blue-500"></span> Trial
                      </span>
                    </div>
                    <p class="text-[13px] text-muted-foreground mt-0.5">
                      <template v-if="subData.status === 'trial'">Full {{ subData.plan?.name ?? 'Premium' }} access during your trial · no card charged</template>
                      <template v-else-if="subData.plan?.plan_type === 'free'">Free plan · upgrade any time for higher limits</template>
                      <template v-else>Paid plan · billed in PHP</template>
                    </p>
                  </div>
                  <div v-if="subData.plan?.plan_type !== 'free' && subData.status !== 'trial'" class="text-right shrink-0">
                    <p class="text-xl sm:text-2xl font-extrabold text-foreground leading-none">
                      ₱{{ Number(subData.plan?.price ?? 0).toLocaleString('en-PH') }}
                    </p>
                    <p class="text-[12px] text-muted-foreground mt-1">per month</p>
                  </div>
                </div>

                <!-- Billing dates row (paid + trial) -->
                <div v-if="subData.plan?.plan_type !== 'free'" class="px-5 sm:px-6 pb-2">
                  <div class="grid grid-cols-3 divide-x divide-border rounded-xl bg-muted/40 border border-border">
                    <div class="px-3 sm:px-4 py-3">
                      <p class="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Started</p>
                      <p class="text-[13px] font-semibold text-foreground mt-1">{{ subFormatDate(subData.start_date) }}</p>
                    </div>
                    <div class="px-3 sm:px-4 py-3">
                      <p class="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{{ subData.status === 'expired' ? 'Expired' : 'Expires' }}</p>
                      <p class="text-[13px] font-semibold text-foreground mt-1">{{ subFormatDate(subData.end_date) }}</p>
                    </div>
                    <div class="px-3 sm:px-4 py-3">
                      <p class="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Days left</p>
                      <p class="text-[13px] font-semibold text-foreground mt-1">
                        <span v-if="subData.days_remaining != null">{{ subData.days_remaining }} day{{ subData.days_remaining !== 1 ? 's' : '' }}</span>
                        <span v-else class="text-muted-foreground">—</span>
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Low-days warning -->
                <div v-if="subData.days_remaining != null && subData.days_remaining <= 7 && subData.status === 'active'" class="px-5 sm:px-6 pt-4">
                  <div class="flex items-start gap-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-[13px] text-amber-700 dark:text-amber-300">
                    <i class="fas fa-clock mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"></i>
                    <span>Your plan expires in <strong class="font-bold">{{ subData.days_remaining }} day{{ subData.days_remaining !== 1 ? 's' : '' }}</strong>. Renew to keep uninterrupted access.</span>
                  </div>
                </div>

                <!-- Expired banner -->
                <div v-if="subData.status === 'expired'" class="px-5 sm:px-6 pt-4">
                  <div class="flex items-start gap-2.5 rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-[13px] text-red-700 dark:text-red-300">
                    <i class="fas fa-exclamation-circle mt-0.5 shrink-0 text-red-600 dark:text-red-400"></i>
                    <span>Your subscription has ended. You can keep using Applypa on the Free plan, or renew for higher limits.</span>
                  </div>
                </div>

                <!-- Actions -->
                <div class="px-5 sm:px-6 py-5 flex flex-wrap items-center gap-3">
                  <router-link
                    to="/subscription/plans"
                    class="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 text-white px-5 py-2.5 text-[13.5px] font-bold shadow-lg shadow-indigo-500/25 hover:brightness-105 transition-all"
                  >
                    <i class="fas fa-arrow-up text-[11px]"></i>
                    {{ subData.plan?.plan_type === 'free' || subData.status === 'trial' ? 'Upgrade Plan' : 'Change Plan' }}
                  </router-link>
                </div>
              </section>

              <!-- ── USAGE CARD ── -->
              <section class="bg-card rounded-xl border border-border shadow-sm p-5 sm:p-6">
                <h3 class="text-[13px] font-bold uppercase tracking-wider text-muted-foreground mb-5">This month's usage</h3>

                <div class="flex flex-col gap-5">
                  <!-- AI Cover Letters -->
                  <div>
                    <div class="flex items-center justify-between mb-2">
                      <span class="flex items-center gap-2.5 text-[14px] font-semibold text-foreground">
                        <i class="fas fa-wand-magic-sparkles w-4 text-center text-indigo-600 dark:text-indigo-400"></i>
                        AI Cover Letters
                      </span>
                      <span v-if="!usageLoading && aiQuota" class="text-[12.5px] text-muted-foreground tabular-nums">
                        <span class="font-bold text-foreground">{{ aiQuota.used }}</span> / {{ aiQuota.limit }}
                      </span>
                      <span v-else-if="usageLoading" class="h-4 w-16 rounded bg-muted animate-pulse"></span>
                      <span v-else class="text-[12.5px] text-muted-foreground">—</span>
                    </div>
                    <div class="h-2 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        v-if="aiQuota && aiQuota.limit > 0"
                        class="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-600 transition-all duration-1000"
                        :style="{ width: Math.min(100, Math.round((aiQuota.used / aiQuota.limit) * 100)) + '%' }"
                      ></div>
                    </div>
                    <p v-if="aiQuota" class="mt-1.5 text-[12px] text-muted-foreground">{{ aiQuota.limit - aiQuota.used }} remaining this month</p>
                  </div>

                  <!-- Job Filter Presets -->
                  <div>
                    <div class="flex items-center justify-between mb-2">
                      <span class="flex items-center gap-2.5 text-[14px] font-semibold text-foreground">
                        <i class="fas fa-filter w-4 text-center text-indigo-600 dark:text-indigo-400"></i>
                        Job Filter Presets
                      </span>
                      <span v-if="!usageLoading" class="text-[12.5px] text-muted-foreground tabular-nums">
                        <span class="font-bold text-foreground">{{ filterPresetsUsed }}</span>
                        / {{ filterPresetsLimit }} saved
                      </span>
                      <span v-else class="h-4 w-16 rounded bg-muted animate-pulse"></span>
                    </div>
                    <div class="h-2 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        class="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-600 transition-all duration-1000"
                        :style="{ width: Math.min(100, Math.round((filterPresetsUsed / filterPresetsLimit) * 100)) + '%' }"
                      ></div>
                    </div>
                    <p class="mt-1.5 text-[12px] text-muted-foreground">
                      {{ filterPresetsLimit - filterPresetsUsed }} slots remaining
                    </p>
                  </div>
                </div>
              </section>

              <!-- ── FEATURES CARD ── -->
              <section class="bg-card rounded-xl border border-border shadow-sm p-5 sm:p-6">
                <p class="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">{{ subData.effective_plan && subData.plan && subData.effective_plan.plan_type !== subData.plan.plan_type ? "What's included on the Free plan" : "What's included" }}</p>
                <div class="h-px bg-border/60 mb-4"></div>

                <ul class="flex flex-col">
                  <li
                    v-for="(feat, i) in subPlanFeatures(subData.effective_plan || subData.plan)"
                    :key="i"
                    class="flex items-center gap-3 py-2.5 border-b border-border/50 last:border-b-0"
                  >
                    <span
                      class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                      :class="feat.disabled ? 'bg-muted text-muted-foreground/70' : 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400'"
                    >
                      <i :class="[feat.icon, 'text-[12px]']"></i>
                    </span>
                    <span class="flex-1 text-sm font-medium" :class="feat.disabled ? 'text-muted-foreground' : 'text-foreground'">{{ feat.label }}</span>
                    <span v-if="feat.value === 'Included' && !feat.disabled" class="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-[11px] font-semibold px-2 py-0.5">
                      <i class="fas fa-check text-[9px]"></i> Included
                    </span>
                    <span v-else-if="feat.value && !feat.disabled" class="rounded-full bg-primary/10 text-primary text-[11px] font-semibold px-2.5 py-0.5 border border-primary/20">{{ feat.value }}</span>
                    <span v-else class="text-[11px] text-muted-foreground/50">Not included</span>
                  </li>
                </ul>
              </section>

              <p class="text-center text-[12px] text-muted-foreground pt-1">
                Questions about billing?
                <router-link to="/contact-us" class="font-semibold text-primary hover:underline">Contact support</router-link>
              </p>
            </template>

            <!-- No subscription state -->
            <div v-else-if="!subLoading" class="bg-card rounded-xl border border-border shadow-sm p-8 text-center">
              <div class="h-14 w-14 mx-auto rounded-2xl bg-muted grid place-items-center mb-4">
                <i class="fas fa-crown text-muted-foreground text-xl"></i>
              </div>
              <h3 class="text-base font-semibold text-foreground">No active subscription</h3>
              <p class="text-sm text-muted-foreground mt-1 mb-4">Choose a plan to unlock premium features.</p>
              <router-link to="/subscription/plans" class="inline-flex items-center gap-2 rounded-xl bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors">
                <i class="fas fa-arrow-up text-[11px]"></i> View Plans
              </router-link>
            </div>
          </div>
          <!-- /Subscription Section -->

        </div>
      </div>
    </div>

    <!-- Disconnect OLJ Confirmation Modal -->
    <div
      v-if="showDisconnectOljModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-card rounded-2xl border border-border max-w-md w-full p-6">
        <h3 class="text-lg font-medium text-foreground mb-4">
          Disconnect Online Jobs Portal
        </h3>
        <p class="text-muted-foreground mb-4">
          Are you sure you want to disconnect your Online Jobs Portal account?
          Your saved applications and sync settings will be lost.
        </p>

        <div class="mt-6 flex justify-end space-x-3">
          <button
            @click="showDisconnectOljModal = false"
            class="px-4 py-2 bg-muted hover:bg-accent text-muted-foreground rounded-lg transition duration-200"
          >
            Cancel
          </button>

          <button
            @click="disconnectOlj"
            class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition duration-200"
            :disabled="integrationsSaving"
          >
            <span v-if="integrationsSaving">
              <i class="fas fa-spinner fa-spin mr-1"></i> Disconnecting...
            </span>
            <span v-else>Disconnect</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Connect Existing OLJ Account Modal -->
    <div
      v-if="showOljConnectForm"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-card border border-border rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div class="flex items-center space-x-3 mb-6">
          <div
            class="h-12 w-12 bg-blue-500/15 rounded-xl flex items-center justify-center"
          >
            <i class="fas fa-link text-blue-600 dark:text-blue-400 text-xl"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-foreground">
              Connect OLJ Account
            </h3>
            <p class="text-sm text-muted-foreground">
              Enter your existing OnlineJobs.ph credentials
            </p>
          </div>
        </div>

        <form @submit.prevent="saveOljCredentials" class="space-y-4">
          <div>
            <label
              for="oljEmail"
              class="block text-sm font-medium text-muted-foreground mb-1.5"
            >
              Email Address
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <i class="fas fa-envelope text-muted-foreground"></i>
              </div>
              <input
                v-model="integrations.olj_email"
                id="oljEmail"
                type="email"
                class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="your@email.com"
                required
              />
            </div>
          </div>

          <div>
            <label
              for="oljPassword"
              class="block text-sm font-medium text-muted-foreground mb-1.5"
            >
              Password
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <i class="fas fa-lock text-muted-foreground"></i>
              </div>
              <input
                v-model="integrations.olj_password"
                id="oljPassword"
                :type="showOljConnectPassword ? 'text' : 'password'"
                class="w-full pl-10 pr-11 py-2.5 rounded-xl border border-input bg-background focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                class="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                @click="showOljConnectPassword = !showOljConnectPassword"
                :aria-label="
                  showOljConnectPassword
                    ? 'Hide OLJ password'
                    : 'Show OLJ password'
                "
              >
                <i
                  :class="
                    showOljConnectPassword ? 'fas fa-eye-slash' : 'fas fa-eye'
                  "
                ></i>
              </button>
            </div>
            <p class="mt-1.5 text-xs text-muted-foreground">
              <i class="fas fa-shield-alt mr-1 text-blue-600 dark:text-blue-400"></i>
              Your password is securely encrypted before storage
            </p>
          </div>

          <div
            v-if="integrationsSaving"
            class="flex items-center p-3 bg-blue-500/10 rounded-xl"
          >
            <i class="fas fa-spinner fa-spin text-blue-600 dark:text-blue-400 mr-2"></i>
            <p class="text-xs text-blue-700 dark:text-blue-300">
              Connecting to OnlineJobs.ph{{ oljConnectElapsedSeconds > 0 ? ` (${oljConnectElapsedSeconds}s)` : "" }}
              — this can take up to a minute, please don't close this page.
            </p>
          </div>
          <div v-else class="flex items-center p-3 bg-blue-500/10 rounded-xl">
            <i class="fas fa-info-circle text-blue-600 dark:text-blue-400 mr-2"></i>
            <p class="text-xs text-blue-700 dark:text-blue-300">
              We'll verify your credentials with OnlineJobs.ph to ensure they're
              correct.
            </p>
          </div>

          <div class="flex space-x-3 pt-2">
            <button
              type="button"
              @click="
                showOljConnectForm = false;
                resetOljForm();
              "
              class="flex-1 px-4 py-2.5 bg-muted hover:bg-accent text-muted-foreground rounded-xl font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-blue-600 disabled:hover:to-indigo-600"
              :disabled="integrationsSaving"
            >
              <span
                v-if="integrationsSaving"
                class="flex items-center justify-center"
              >
                <i class="fas fa-spinner fa-spin mr-2"></i>
                Connecting{{ oljConnectElapsedSeconds > 0 ? ` (${oljConnectElapsedSeconds}s)` : "" }}...
              </span>
              <span v-else class="flex items-center justify-center">
                <i class="fas fa-plug mr-2"></i>
                Connect Account
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Create New OLJ Account Modal (only when feature flag enabled) -->
    <div
      v-if="showOljCreateForm && oljCreateAccountEnabled"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-card border border-border rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div class="flex items-center space-x-3 mb-6">
          <div
            class="h-12 w-12 bg-green-500/15 rounded-xl flex items-center justify-center"
          >
            <i class="fas fa-user-plus text-green-600 dark:text-green-400 text-xl"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-foreground">
              Create OLJ Account
            </h3>
            <p class="text-sm text-muted-foreground">
              Create a new OnlineJobs.ph account
            </p>
          </div>
        </div>

        <form @submit.prevent="createOljAccount" class="space-y-4">
          <div>
            <label
              for="newOljEmail"
              class="block text-sm font-medium text-muted-foreground mb-1.5"
            >
              Email Address
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <i class="fas fa-envelope text-muted-foreground"></i>
              </div>
              <input
                v-model="newOljAccountForm.email"
                id="newOljEmail"
                type="email"
                class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                placeholder="your@email.com"
                required
              />
            </div>
          </div>

          <div>
            <label
              for="newOljPassword"
              class="block text-sm font-medium text-muted-foreground mb-1.5"
            >
              Password
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <i class="fas fa-lock text-muted-foreground"></i>
              </div>
              <input
                v-model="newOljAccountForm.password"
                id="newOljPassword"
                type="password"
                class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                placeholder="Create a password (min 6 characters)"
                required
                minlength="6"
              />
            </div>
            <p class="mt-1.5 text-xs text-muted-foreground">
              Must be at least 6 characters long
            </p>
          </div>

          <div>
            <label
              for="confirmOljPassword"
              class="block text-sm font-medium text-muted-foreground mb-1.5"
            >
              Confirm Password
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <i class="fas fa-lock text-muted-foreground"></i>
              </div>
              <input
                v-model="newOljAccountForm.confirmPassword"
                id="confirmOljPassword"
                type="password"
                class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                placeholder="Confirm your password"
                required
              />
            </div>
          </div>

          <div class="flex items-start p-3 bg-amber-500/10 rounded-xl">
            <i
              class="fas fa-exclamation-triangle text-amber-600 dark:text-amber-400 mr-2 mt-0.5"
            ></i>
            <div class="text-xs text-amber-700 dark:text-amber-300 space-y-1">
              <p>After creating your account:</p>
              <ul class="list-disc list-inside ml-1">
                <li>You'll need to verify your email on OnlineJobs.ph</li>
                <li>Your account will be reviewed before approval</li>
              </ul>
            </div>
          </div>

          <div class="flex space-x-3 pt-2">
            <button
              type="button"
              @click="
                showOljCreateForm = false;
                resetNewOljForm();
              "
              class="flex-1 px-4 py-2.5 bg-muted hover:bg-accent text-muted-foreground rounded-xl font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="flex-1 px-4 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-medium shadow-lg shadow-green-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-green-600 disabled:hover:to-emerald-600"
              :disabled="
                integrationsSaving ||
                newOljAccountForm.password !== newOljAccountForm.confirmPassword
              "
            >
              <span
                v-if="integrationsSaving"
                class="flex items-center justify-center"
              >
                <i class="fas fa-spinner fa-spin mr-2"></i>
                Creating...
              </span>
              <span v-else class="flex items-center justify-center">
                <i class="fas fa-user-plus mr-2"></i>
                Create Account
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Update OLJ Credentials Modal -->
    <div
      v-if="showUpdateOljForm"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-card border border-border rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div class="flex items-center space-x-3 mb-6">
          <div
            class="h-12 w-12 bg-indigo-500/15 rounded-xl flex items-center justify-center"
          >
            <i class="fas fa-edit text-indigo-600 dark:text-indigo-400 text-xl"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-foreground">
              {{ oljNeedsReconnect ? "Reconnect OnlineJobs.ph" : "Update OLJ Credentials" }}
            </h3>
            <p class="text-sm text-muted-foreground">
              {{
                oljNeedsReconnect
                  ? "Enter your current OnlineJobs.ph email and password"
                  : "Update your OnlineJobs.ph login information"
              }}
            </p>
          </div>
        </div>

        <form @submit.prevent="updateOljCredentials" class="space-y-4">
          <div>
            <label
              for="updateOljEmail"
              class="block text-sm font-medium text-muted-foreground mb-1.5"
            >
              Email Address
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <i class="fas fa-envelope text-muted-foreground"></i>
              </div>
              <input
                v-model="updateOljForm.email"
                id="updateOljEmail"
                type="email"
                class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                placeholder="your@email.com"
                required
              />
            </div>
          </div>

          <div>
            <label
              for="updateOljPassword"
              class="block text-sm font-medium text-muted-foreground mb-1.5"
            >
              {{ oljNeedsReconnect ? "Current OnlineJobs.ph Password" : "New Password" }}
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <i class="fas fa-lock text-muted-foreground"></i>
              </div>
              <input
                v-model="updateOljForm.password"
                id="updateOljPassword"
                type="password"
                class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                :placeholder="oljNeedsReconnect ? 'Enter your current OnlineJobs.ph password' : 'Enter new password'"
                required
              />
            </div>
            <p class="mt-1.5 text-xs text-muted-foreground">
              <i class="fas fa-shield-alt mr-1 text-indigo-500"></i>
              Your password is securely encrypted before storage
            </p>
          </div>

          <div class="flex items-center p-3 bg-indigo-500/10 rounded-xl">
            <i class="fas fa-info-circle text-indigo-600 dark:text-indigo-400 mr-2"></i>
            <p class="text-xs text-indigo-700 dark:text-indigo-300">
              We'll verify your new credentials with OnlineJobs.ph.
            </p>
          </div>

          <div class="flex space-x-3 pt-2">
            <button
              type="button"
              @click="showUpdateOljForm = false"
              class="flex-1 px-4 py-2.5 bg-muted hover:bg-accent text-muted-foreground rounded-xl font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl font-medium shadow-lg shadow-indigo-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-indigo-600 disabled:hover:to-purple-600"
              :disabled="integrationsSaving"
            >
              <span
                v-if="integrationsSaving"
                class="flex items-center justify-center"
              >
                <i class="fas fa-spinner fa-spin mr-2"></i>
                {{ oljNeedsReconnect ? "Reconnecting..." : "Updating..." }}
              </span>
              <span v-else class="flex items-center justify-center">
                <i class="fas mr-2" :class="oljNeedsReconnect ? 'fa-plug' : 'fa-save'"></i>
                {{ oljNeedsReconnect ? "Reconnect" : "Update Credentials" }}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Connect JobStreet Account Modal -->
    <div
      v-if="showJobStreetConnectForm"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-card border border-border rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div class="flex items-center space-x-3 mb-6">
          <div
            class="h-12 w-12 bg-blue-500/15 rounded-xl flex items-center justify-center"
          >
            <i class="fas fa-link text-blue-600 dark:text-blue-400 text-xl"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-foreground">
              Connect JobStreet Account
            </h3>
            <p class="text-sm text-muted-foreground">
              {{ jobstreetStep === 1 ? 'Enter your email to receive a code' : 'Enter the verification code' }}
            </p>
          </div>
        </div>

        <form @submit.prevent="saveJobStreetCredentials" class="space-y-4">
          <!-- Step 1: Email Input -->
          <div v-if="jobstreetStep === 1">
            <div>
              <label
                for="jobstreetEmail"
                class="block text-sm font-medium text-muted-foreground mb-1.5"
              >
                Email Address
              </label>
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                >
                  <i class="fas fa-envelope text-muted-foreground"></i>
                </div>
                <input
                  v-model="jobstreetForm.email"
                  id="jobstreetEmail"
                  type="email"
                  class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="your@email.com"
                  required
                />
              </div>
              <p class="mt-1.5 text-xs text-muted-foreground">
                <i class="fas fa-info-circle mr-1 text-blue-500"></i>
                We'll send a verification code to this email
              </p>
            </div>
          </div>

          <!-- Step 2: OTP Input -->
          <div v-else>
            <div class="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-4">
              <p class="text-sm text-green-700 dark:text-green-300">
                <i class="fas fa-envelope-open-text mr-1"></i>
                Check <strong>{{ jobstreetForm.email }}</strong> for the 6-digit code
              </p>
            </div>
            <div>
              <label
                for="jobstreetOtp"
                class="block text-sm font-medium text-muted-foreground mb-1.5"
              >
                Verification Code (6 digits)
              </label>
              <input
                v-model="jobstreetForm.otp"
                id="jobstreetOtp"
                type="text"
                maxlength="6"
                class="w-full px-4 py-2.5 rounded-xl border border-input bg-background focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-center text-2xl font-mono tracking-widest"
                placeholder="000000"
                required
              />
            </div>
          </div>

          <div class="flex space-x-3 pt-2">
            <button
              type="button"
              @click="showJobStreetConnectForm = false; jobstreetStep = 1"
              class="flex-1 px-4 py-2.5 bg-muted hover:bg-accent text-muted-foreground rounded-xl font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-blue-600 disabled:hover:to-indigo-600"
              :disabled="integrationsSaving"
            >
              <span
                v-if="integrationsSaving"
                class="flex items-center justify-center"
              >
                <i class="fas fa-spinner fa-spin mr-2"></i>
                {{ jobstreetStep === 1 ? 'Sending...' : 'Verifying...' }}
              </span>
              <span v-else class="flex items-center justify-center">
                <i :class="['mr-2', jobstreetStep === 1 ? 'fas fa-paper-plane' : 'fas fa-check']"></i>
                {{ jobstreetStep === 1 ? 'Send Code' : 'Verify & Connect' }}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Disconnect JobStreet Confirmation Modal -->
    <div
      v-if="showDisconnectJobStreetModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-card rounded-2xl border border-border max-w-md w-full p-6">
        <h3 class="text-lg font-medium text-foreground mb-4">
          Disconnect JobStreet
        </h3>
        <p class="text-muted-foreground mb-4">
          Are you sure you want to disconnect your JobStreet account?
          Your stored session will be removed.
        </p>

        <div class="mt-6 flex justify-end space-x-3">
          <button
            @click="showDisconnectJobStreetModal = false"
            class="px-4 py-2 bg-muted hover:bg-accent text-muted-foreground rounded-lg transition duration-200"
          >
            Cancel
          </button>

          <button
            @click="disconnectJobStreet"
            class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition duration-200"
            :disabled="integrationsSaving"
          >
            <span v-if="integrationsSaving">
              <i class="fas fa-spinner fa-spin mr-1"></i> Disconnecting...
            </span>
            <span v-else>Disconnect</span>
          </button>
        </div>
      </div>
    </div>

    <!-- HIDDEN: LinkedIn -->
    <template v-if="false">
    <!-- LinkedIn Connect Modal -->
    <div
      v-if="showLinkedInConnectForm"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-card border border-border rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div class="flex items-center space-x-3 mb-6">
          <div
            class="h-12 w-12 bg-blue-500/15 rounded-xl flex items-center justify-center"
          >
            <i class="fab fa-linkedin text-blue-600 text-2xl"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-foreground">
              Connect LinkedIn
            </h3>
            <p class="text-sm text-muted-foreground">Enter your LinkedIn credentials</p>
          </div>
        </div>

        <form @submit.prevent="saveLinkedInCredentials" class="space-y-4">
          <div>
            <label
              for="linkedInEmail"
              class="block text-sm font-medium text-muted-foreground mb-1.5"
            >
              Email Address
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <i class="fas fa-envelope text-muted-foreground"></i>
              </div>
              <input
                v-model="linkedInForm.email"
                id="linkedInEmail"
                type="email"
                class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="your@email.com"
                required
              />
            </div>
          </div>

          <div>
            <label
              for="linkedInPassword"
              class="block text-sm font-medium text-muted-foreground mb-1.5"
            >
              Password
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <i class="fas fa-lock text-muted-foreground"></i>
              </div>
              <input
                v-model="linkedInForm.password"
                id="linkedInPassword"
                type="password"
                class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your password"
                required
              />
            </div>
            <p class="mt-1.5 text-xs text-muted-foreground">
              <i class="fas fa-shield-alt mr-1 text-blue-400"></i>
              Your password is securely encrypted before storage
            </p>
          </div>

          <div class="flex items-center p-3 bg-blue-500/10 rounded-xl">
            <i class="fas fa-info-circle text-blue-400 mr-2"></i>
            <p class="text-xs text-blue-300">
              We'll verify your credentials with LinkedIn to ensure they're
              correct.
            </p>
          </div>

          <div class="flex space-x-3 pt-2">
            <button
              type="button"
              @click="
                showLinkedInConnectForm = false;
                resetLinkedInForm();
              "
              class="flex-1 px-4 py-2.5 bg-muted hover:bg-accent text-muted-foreground rounded-xl font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-blue-600 disabled:hover:to-indigo-600"
              :disabled="integrationsSaving"
            >
              <span
                v-if="integrationsSaving"
                class="flex items-center justify-center"
              >
                <i class="fas fa-spinner fa-spin mr-2"></i>
                Verifying...
              </span>
              <span v-else class="flex items-center justify-center">
                <i class="fab fa-linkedin mr-2"></i>
                Connect LinkedIn
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
    </template>
    <!-- /HIDDEN: LinkedIn -->

    <!-- HIDDEN: LinkedIn -->
    <template v-if="false">
    <!-- LinkedIn Update Credentials Modal -->
    <div
      v-if="showLinkedInUpdateForm"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-card border border-border rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div class="flex items-center space-x-3 mb-6">
          <div
            class="h-12 w-12 bg-indigo-500/15 rounded-xl flex items-center justify-center"
          >
            <i class="fas fa-edit text-indigo-400 text-xl"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-foreground">
              Update LinkedIn Credentials
            </h3>
            <p class="text-sm text-muted-foreground">
              Update your LinkedIn login information
            </p>
          </div>
        </div>

        <form @submit.prevent="updateLinkedInCredentials" class="space-y-4">
          <div>
            <label
              for="updateLinkedInEmail"
              class="block text-sm font-medium text-muted-foreground mb-1.5"
            >
              Email Address
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <i class="fas fa-envelope text-muted-foreground"></i>
              </div>
              <input
                v-model="updateLinkedInForm.email"
                id="updateLinkedInEmail"
                type="email"
                class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                placeholder="your@email.com"
                required
              />
            </div>
          </div>

          <div>
            <label
              for="updateLinkedInPassword"
              class="block text-sm font-medium text-muted-foreground mb-1.5"
            >
              New Password
            </label>
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <i class="fas fa-lock text-muted-foreground"></i>
              </div>
              <input
                v-model="updateLinkedInForm.password"
                id="updateLinkedInPassword"
                type="password"
                class="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                placeholder="Enter new password"
                required
              />
            </div>
            <p class="mt-1.5 text-xs text-muted-foreground">
              <i class="fas fa-shield-alt mr-1 text-indigo-500"></i>
              Your password is securely encrypted before storage
            </p>
          </div>

          <div class="flex items-center p-3 bg-indigo-500/10 rounded-xl">
            <i class="fas fa-info-circle text-indigo-400 mr-2"></i>
            <p class="text-xs text-indigo-300">
              We'll verify your new credentials with LinkedIn.
            </p>
          </div>

          <div class="flex space-x-3 pt-2">
            <button
              type="button"
              @click="showLinkedInUpdateForm = false"
              class="flex-1 px-4 py-2.5 bg-muted hover:bg-accent text-muted-foreground rounded-xl font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl font-medium shadow-lg shadow-indigo-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-indigo-600 disabled:hover:to-purple-600"
              :disabled="integrationsSaving"
            >
              <span
                v-if="integrationsSaving"
                class="flex items-center justify-center"
              >
                <i class="fas fa-spinner fa-spin mr-2"></i>
                Updating...
              </span>
              <span v-else class="flex items-center justify-center">
                <i class="fas fa-save mr-2"></i>
                Update Credentials
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
    </template>
    <!-- /HIDDEN: LinkedIn -->

    <!-- HIDDEN: LinkedIn -->
    <template v-if="false">
    <!-- Disconnect LinkedIn Confirmation Modal -->
    <div
      v-if="showDisconnectLinkedInModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-card rounded-2xl border border-border max-w-md w-full p-6">
        <h3 class="text-lg font-medium text-foreground mb-4">
          Disconnect LinkedIn
        </h3>
        <p class="text-muted-foreground mb-4">
          Are you sure you want to disconnect your LinkedIn account? Your synced
          profile data and imported applications will no longer be updated.
        </p>

        <div class="mt-6 flex justify-end space-x-3">
          <button
            @click="showDisconnectLinkedInModal = false"
            class="px-4 py-2 bg-muted hover:bg-accent text-muted-foreground rounded-lg transition duration-200"
          >
            Cancel
          </button>

          <button
            @click="disconnectLinkedIn"
            class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition duration-200"
            :disabled="integrationsSaving"
          >
            <span v-if="integrationsSaving">
              <i class="fas fa-spinner fa-spin mr-1"></i> Disconnecting...
            </span>
            <span v-else>Disconnect</span>
          </button>
        </div>
      </div>
    </div>
    </template>
    <!-- /HIDDEN: LinkedIn -->

    <!-- Delete Account Confirmation Modal -->
    <div
      v-if="showDeleteAccountModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-card border border-border rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div class="flex items-center gap-3 mb-5">
          <div class="h-12 w-12 bg-red-500/15 rounded-xl flex items-center justify-center shrink-0">
            <i class="fas fa-exclamation-triangle text-red-600 dark:text-red-400 text-xl"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-foreground">Delete Account</h3>
            <p class="text-sm text-muted-foreground">This action cannot be undone</p>
          </div>
        </div>

        <div class="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-5">
          <p class="text-sm text-red-700 dark:text-red-300">
            Your account will be <strong>permanently deactivated</strong>. You will be logged out immediately and will no longer be able to sign in.
          </p>
        </div>

        <form @submit.prevent="deleteAccount" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-muted-foreground mb-1.5">
              Confirm with your password
            </label>
            <div class="relative">
              <input
                v-model="deleteAccountPassword"
                :type="showDeletePassword ? 'text' : 'password'"
                class="w-full px-4 py-2.5 pr-11 rounded-xl border border-input bg-background focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm transition-colors"
                placeholder="Enter your current password"
                required
              />
              <button
                type="button"
                class="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                @click="showDeletePassword = !showDeletePassword"
              >
                <i :class="showDeletePassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>

          <div class="flex gap-3 pt-2">
            <button
              type="button"
              @click="showDeleteAccountModal = false; deleteAccountPassword = ''; showDeletePassword = false"
              class="flex-1 px-4 py-2.5 bg-muted hover:bg-accent text-muted-foreground rounded-xl font-medium text-sm transition-colors"
              :disabled="deleteAccountLoading"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              :disabled="deleteAccountLoading || !deleteAccountPassword"
            >
              <span v-if="deleteAccountLoading">
                <i class="fas fa-spinner fa-spin mr-2"></i>Deleting...
              </span>
              <span v-else>Delete My Account</span>
            </button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, watch, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import apiClient from "@/services/api";
import Toast from "@/components/Toast.vue";
import authStore from "@/store/auth";
import { resolvePlanFeatures, coverLettersCell, autoApplyCell, directApplyCell } from "@/composables/useSubscriptionPlans";
import { OLJ_WRONG_LOGIN_MESSAGE } from "@/utils/oljConnection";

export default {
  name: "SettingsPage",
  components: {
    Toast,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
const loading = ref(true);
    const error = ref(null);
    const sectionDropdownOpen = ref(false);

    // Initialize activeSection from route query if present, otherwise default to 'personal'
    const activeSection = ref(
      route.query.section && typeof route.query.section === "string"
        ? route.query.section
        : "personal",
    );

    // Toast state
    const showToast = ref(false);
    const toastType = ref("success");
    const toastTitle = ref("");
    const toastMessage = ref("");

    // Handle image error
    const handleImageError = (event) => {
      event.target.style.display = 'none';
      // Show fallback avatar if image fails to load
    };

    // Show toast notification
    const showToastNotification = (type, title, message) => {
      // Reset any existing toast first to ensure animation works properly
      showToast.value = false;

      // Force a DOM update before showing the new toast
      setTimeout(() => {
        toastType.value = type;
        toastTitle.value = title;
        toastMessage.value = message;
        showToast.value = true;

        // Auto-hide toast after 5 seconds
        setTimeout(() => {
          showToast.value = false;
        }, 5000);
      }, 10);
    };

    // Settings sections
    const settingsSections = [
      { id: "personal", title: "Account Information", icon: "fas fa-user" },
      { id: "security", title: "Security", icon: "fas fa-lock" },
      { id: "subscription", title: "Subscription", icon: "fas fa-crown" },
    ];

    // Subscription section state
    const subData = ref(null);
    const subLoading = ref(false);
    const subError = ref(null);
    const aiQuota = ref(null);
    const filterPresetsUsed = ref(0);
    const filterPresetsLimit = ref(3);
    const usageLoading = ref(false);

    const subPlanMeta = {
      basic:   { icon: "fas fa-star",         gradient: "from-blue-500 to-indigo-600",   shadow: "shadow-blue-500/25" },
      premium: { icon: "fas fa-crown",        gradient: "from-indigo-500 to-violet-600", shadow: "shadow-indigo-500/25" },
      free:    { icon: "fas fa-check-circle", gradient: "from-green-500 to-emerald-600", shadow: "shadow-green-500/25" },
    };

    const subFormatDate = (iso) => {
      if (!iso) return "—";
      return new Date(iso).toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" });
    };

    const subPlanFeatures = (plan) => {
      if (!plan) plan = {};
      const planType = plan.plan_type ?? 'free';

      // Limit convention: 0 = not included, 99999999 = unlimited (see useSubscriptionPlans).
      const f = resolvePlanFeatures(plan, planType);
      const cellOrNull = (text) => (text === "Not included" ? null : text);
      const applyValue = cellOrNull(autoApplyCell(f));
      const manualApplyValue = cellOrNull(directApplyCell(f));
      const coverValue = cellOrNull(coverLettersCell(f));
      const filterValue = f.max_job_filter_presets > 0 ? `${f.max_job_filter_presets} presets` : null;
      const hasTelegram = f.has_telegram_alerts;
      const hasEmail = f.has_email_notifications;
      const hasPriority = f.has_priority_support;
      const telegramLabel = "Telegram & Discord alerts";
      // Automatic follow-ups are hidden on every plan for now (see config/features.js).

      const rows = [
        { icon: "fas fa-robot",               label: "Auto Apply",              value: applyValue,   disabled: !applyValue },
        { icon: "fas fa-paper-plane",         label: "Direct Apply",            value: manualApplyValue, disabled: !manualApplyValue },
        { icon: "fas fa-wand-magic-sparkles", label: "AI Cover Letters",        value: coverValue,   disabled: !coverValue },
        { icon: "fas fa-sliders-h",           label: "Job Filters",             value: filterValue,  disabled: !filterValue },
        { icon: "fas fa-brain",               label: "AI Instructions",         value: planType === 'free' ? '1' : 'Unlimited' },
        { icon: "fab fa-telegram",            label: telegramLabel,             value: hasTelegram ? "Included" : null, disabled: !hasTelegram },
        { icon: "fas fa-envelope",            label: "Email notifications",     value: hasEmail ? "Included" : null, disabled: !hasEmail },
        // { icon: "fas fa-reply",               label: "AI Follow-ups / month",   value: followupValue, disabled: !followupValue },
        { icon: "fas fa-headset",             label: "Priority support",        value: hasPriority ? "Included" : null, disabled: !hasPriority },
      ];

      if (planType === 'premium') {
        rows.push(
          { icon: "fas fa-bolt",  label: "Priority Queue",       value: "Included" },
          { icon: "fas fa-star",  label: "Early Feature Access", value: "Included" },
        );
      }

      return rows;
    };

    const fetchSubData = async () => {
      if (subData.value) return;
      subLoading.value = true;
      subError.value = null;
      try {
        const res = await apiClient.get("/auth/subscription/current/");
        subData.value = res.data;
      } catch (err) {
        subError.value = "Could not load subscription data.";
      } finally {
        subLoading.value = false;
      }
      // Load usage non-blocking
      usageLoading.value = true;
      Promise.allSettled([
        apiClient.get("/dashboard/summary/"),
        apiClient.get("/job-filter-presets/"),
      ]).then(([dashRes, filtersRes]) => {
        if (dashRes.status === "fulfilled") {
          aiQuota.value = dashRes.value.data?.sidebar?.ai_quota ?? null;
          if (dashRes.value.data?.max_job_filter_presets != null) {
            filterPresetsLimit.value = dashRes.value.data.max_job_filter_presets;
          }
        }
        if (filtersRes.status === "fulfilled") {
          const d = filtersRes.value.data;
          filterPresetsUsed.value = Array.isArray(d) ? d.length : (d?.results?.length ?? d?.count ?? 0);
        }
        usageLoading.value = false;
      });
    };

    // User settings state
    const userSettings = reactive({
      username: "",
      email: "",
      first_name: "",
      last_name: "",
      phone_number: "",
      account_type: "jobseeker",
      ai_instructions: "",
      profile_picture: "",
      title: "",
      about: "",
      skills: "",
      personal_context: "",
    });

    // Admin status
    const isAdmin = computed(
      () =>
        !!authStore.state.user &&
        (authStore.state.user.is_superuser ||
          authStore.state.user.account_type === "admin" ||
          authStore.state.user.admin_profile),
    );

    const userSubscriptionPlanType = ref("free");
    const userIsTrialActive = ref(false);

    const isFreeUser = computed(() => userSubscriptionPlanType.value === "free");

    const canUseLinkedInIntegration = computed(
      () =>
        userSubscriptionPlanType.value === "basic" ||
        userSubscriptionPlanType.value === "premium" ||
        (userSubscriptionPlanType.value === "free" && userIsTrialActive.value),
    );

    const oljUnlocked = computed(() => !!authStore.state.user?.olj_jobs_enabled);

    const jobActivity = reactive({
      loading: false,
      error: null,
      appliedCount: 0,
      savedCount: 0,
      interviewsCount: 0,
    });

    const fetchJobActivity = async () => {
      jobActivity.loading = true;
      jobActivity.error = null;

      try {
        const [applicationsResponse, savedJobsResponse] = await Promise.all([
          apiClient.get("/job-applications/list_applications/"),
          apiClient.get("/saved-jobs/"),
        ]);

        const applications = Array.isArray(applicationsResponse.data)
          ? applicationsResponse.data
          : [];
        const savedJobs = Array.isArray(savedJobsResponse.data)
          ? savedJobsResponse.data
          : savedJobsResponse.data?.results && Array.isArray(savedJobsResponse.data.results)
            ? savedJobsResponse.data.results
            : [];

        jobActivity.appliedCount = applications.length;
        jobActivity.interviewsCount = applications.filter(
          (a) => (a.status || "").toLowerCase() === "interview",
        ).length;
        jobActivity.savedCount = savedJobs.length;
      } catch (err) {
        console.error("Error loading job activity:", err);
        jobActivity.error = "Failed to load activity. Please try again.";
      } finally {
        jobActivity.loading = false;
      }
    };

    // Password form
    const passwordForm = reactive({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

    const showCurrentPassword = ref(false);
    const showNewPassword = ref(false);
    const showConfirmPassword = ref(false);
    const showOljConnectPassword = ref(false);

    // Delete account state
    const showDeleteAccountModal = ref(false);
    const deleteAccountPassword = ref('');
    const deleteAccountLoading = ref(false);
    const showDeletePassword = ref(false);

    const calculatePasswordStrength = (password) => {
      if (!password) {
        return { label: "Weak", score: 0 };
      }

      let score = 0;

      if (password.length >= 8) score += 1;
      if (password.length >= 12) score += 1;
      if (/[a-z]/.test(password)) score += 1;
      if (/[A-Z]/.test(password)) score += 1;
      if (/\d/.test(password)) score += 1;
      if (/[^A-Za-z0-9]/.test(password)) score += 1;

      const normalized = password.toLowerCase();
      const hasObviousPattern =
        normalized.includes("password") ||
        normalized.includes("qwerty") ||
        normalized.includes("123456") ||
        normalized.includes("111111");
      if (hasObviousPattern) score = Math.max(0, score - 2);

      if (score <= 2) return { label: "Weak", score };
      if (score <= 4) return { label: "Medium", score };
      return { label: "Strong", score };
    };

    const passwordStrength = computed(() =>
      calculatePasswordStrength(passwordForm.newPassword),
    );

    const passwordStrengthClass = computed(() => {
      if (!passwordForm.newPassword) return "bg-muted text-muted-foreground";
      if (passwordStrength.value.label === "Weak") return "bg-red-500/15 text-red-700 dark:text-red-300";
      if (passwordStrength.value.label === "Medium")
        return "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300";
      return "bg-green-500/15 text-green-700 dark:text-green-300";
    });

    const passwordStrengthBarClass = computed(() => {
      if (!passwordForm.newPassword) return "bg-muted";
      if (passwordStrength.value.label === "Weak") return "bg-red-500";
      if (passwordStrength.value.label === "Medium") return "bg-yellow-500";
      return "bg-green-500";
    });

    const passwordStrengthPercent = computed(() => {
      if (!passwordForm.newPassword) return 0;
      const score = passwordStrength.value.score;
      return Math.min(100, Math.max(10, Math.round((score / 6) * 100)));
    });

    const passwordRequirements = computed(() => {
      const pw = passwordForm.newPassword || "";
      return {
        length: pw.length >= 8,
        uppercase: /[A-Z]/.test(pw),
        number: /\d/.test(pw),
      };
    });

    const isPasswordStrongEnough = computed(() => {
      return (
        !passwordForm.newPassword ||
        passwordStrength.value.label === "Medium" ||
        passwordStrength.value.label === "Strong"
      );
    });

    // Notification settings
    const notificationSettings = reactive({
      job_alerts: true,
      application_updates: true,
      messages: true,
      marketing: false,
      frequency: "daily",
    });

    // Integration settings
    const integrations = reactive({
      olj_connected: false,
      olj_email: "",
      olj_password: "",
      olj_sync_applications: true,
      integrationId: null,
      olj_id_proof: null,
      // "connected" | "needs_reconnect" — set when OLJ rejects the saved login
      olj_connection_status: null,
      olj_connection_status_reason: "",
      // New: Track pending verification state for newly created accounts
      olj_pending_verification: false,
      olj_pending_email: "",
      // LinkedIn integration
      linkedin_connected: false,
      linkedin_email: "",
      linkedin_password: "",
      linkedin_integration_id: null,
      linkedin_sync_applications: true,
      linkedin_sync_profile: true,

      // JobStreet integration
      jobstreet_connected: false,
      jobstreet_integration_id: null,
      jobstreet_email: "",
    });

    // Privacy settings
    const privacySettings = reactive({
      profile_visibility: "public",
      allow_recruiter_contact: true,
      hide_current_employer: false,
    });

    // Admin Tools state
    const adminToolsLoading = ref({
      scrapeJobs: false,
      scrapeJobHunters: false,
      verifyLinkedIn: false,
      syncApplications: false,
      cleanupJobs: false,
      refreshEmployerTotals: false,
    });
    const adminToolsLogs = ref([]);
    const adminStats = ref(null);

    // Status tracking for each tool (idle, running, success, error)
    const adminToolsStatus = ref({
      scrapeJobs: { status: "idle", message: "" },
      scrapeJobHunters: { status: "idle", message: "" },
      syncApplications: { status: "idle", message: "" },
      cleanupJobs: { status: "idle", message: "" },
      refreshEmployers: { status: "idle", message: "" },
    });

    // Polling intervals for background job tracking
    const statusPollIntervals = ref({});

    // Poll job status to track when background tasks actually complete
    const startStatusPolling = (
      toolName,
      statusEndpoint,
      intervalMs = 5000,
    ) => {
      // Clear any existing poll for this tool
      if (statusPollIntervals.value[toolName]) {
        clearInterval(statusPollIntervals.value[toolName]);
      }

      adminToolsStatus.value[toolName] = {
        status: "running",
        message: "Running...",
      };

      const poll = async () => {
        try {
          const response = await apiClient.get(statusEndpoint);
          const isRunning =
            response.data?.running || response.data?.in_progress;

          if (!isRunning) {
            // Job is complete
            adminToolsStatus.value[toolName] = {
              status: "success",
              message: "Completed",
            };
            clearInterval(statusPollIntervals.value[toolName]);
            delete statusPollIntervals.value[toolName];
          }
          // If still running, keep polling
        } catch (err) {
          console.error(`Error polling ${toolName} status:`, err);
          // On error, stop polling and show error state
          adminToolsStatus.value[toolName] = {
            status: "error",
            message: "Status check failed",
          };
          clearInterval(statusPollIntervals.value[toolName]);
          delete statusPollIntervals.value[toolName];
        }
      };

      // Start polling
      statusPollIntervals.value[toolName] = setInterval(poll, intervalMs);
      // Initial poll
      poll();
    };

    // Stop all polling on component unmount
    const stopAllPolling = () => {
      Object.keys(statusPollIntervals.value).forEach((toolName) => {
        clearInterval(statusPollIntervals.value[toolName]);
      });
      statusPollIntervals.value = {};
    };

    // Cleanup polling when component unmounts
    onUnmounted(() => {
      stopAllPolling();
    });

    // Scrape locking state (like AdminDashboard)
    const scrapeLockedUntil = ref(null);
    const scrapeMessage = ref(null);
    const scrapeError = ref(null);
    const SCRAPE_LOCK_KEY = "adminToolsOljScrapeLockedUntil";

    const isScrapeLocked = computed(() => {
      if (!scrapeLockedUntil.value) return false;
      return Date.now() < scrapeLockedUntil.value;
    });

    const persistLock = (timestamp) => {
      if (!timestamp) {
        localStorage.removeItem(SCRAPE_LOCK_KEY);
        return;
      }
      localStorage.setItem(SCRAPE_LOCK_KEY, String(timestamp));
    };

    const hydrateLock = () => {
      const raw = localStorage.getItem(SCRAPE_LOCK_KEY);
      if (!raw) return;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) return;
      scrapeLockedUntil.value = parsed;
    };

    // Admin Tools actions - using AdminDashboard pattern
    const runScrapeJobs = async () => {

      // Check lock like AdminDashboard
      if (adminToolsLoading.value.scrapeJobs || isScrapeLocked.value) return;

      adminToolsLoading.value.scrapeJobs = true;
      adminToolsStatus.value.scrapeJobs = {
        status: "running",
        message: "Starting...",
      };
      scrapeError.value = null;
      scrapeMessage.value = null;

      // Prevent repeated clicks - UX throttle like AdminDashboard
      scrapeLockedUntil.value = Date.now() + 60_000;
      persistLock(scrapeLockedUntil.value);

      try {
        // Use the same endpoint as AdminDashboard
        const response = await apiClient.post(
          "/admin-dashboard/run-olj-scraper/",
        );

        scrapeMessage.value =
          response?.data?.message || "Scraping started.";
        // Status is already 'running' from before the API call
        // Polling will update to 'success' when job actually completes
        adminToolsStatus.value.scrapeJobs = {
          status: "running",
          message: "Running...",
        };

        // Force reactivity by creating new array reference
        adminToolsLogs.value = [
          {
            action: "Scrape Jobs",
            status: "success",
            message:
              response.data.message || "Job scraping started in background",
            timestamp: new Date().toLocaleString(),
          },
          ...adminToolsLogs.value,
        ];
        showToastNotification(
          "success",
          "Success",
          response.data.message || "Job scraping started!",
        );

        // Start polling to track when the background job actually completes
        startStatusPolling(
          "scrapeJobs",
          "/admin-dashboard/run-olj-scraper/status/",
        );
      } catch (err) {
        console.error("[DEBUG] API error:", err);

        if (err.response?.status === 409) {
          // Already running - keep locked and start polling to track when it completes
          scrapeMessage.value =
            err.response?.data?.error || "Scraping is already running.";
          adminToolsStatus.value.scrapeJobs = {
            status: "running",
            message: "Already running",
          };
          scrapeLockedUntil.value = Date.now() + 60_000;
          persistLock(scrapeLockedUntil.value);
          showToastNotification("info", "Info", scrapeMessage.value);
          // Start polling to track when the already-running job completes
          startStatusPolling(
            "scrapeJobs",
            "/admin-dashboard/run-olj-scraper/status/",
          );
        } else {
          scrapeError.value =
            err.response?.data?.error || "Failed to start job scraper.";
          adminToolsStatus.value.scrapeJobs = {
            status: "error",
            message: "Failed to start",
          };
          scrapeLockedUntil.value = null;
          persistLock(null);

          const errorMessage = scrapeError.value;
          adminToolsLogs.value.unshift({
            action: "Scrape Jobs",
            status: "error",
            message: errorMessage,
            timestamp: new Date().toLocaleString(),
          });
          showToastNotification("error", "Error", errorMessage);
        }
      } finally {
        adminToolsLoading.value.scrapeJobs = false;
      }
    };

    const runScrapeJobHunters = async () => {
      adminToolsLoading.value.scrapeJobHunters = true;
      adminToolsStatus.value.scrapeJobHunters = {
        status: "running",
        message: "Starting...",
      };
      try {
        const response = await apiClient.post("/admin/scrape-job-hunters/");
        // Job started - now polling will track when it actually completes
        adminToolsStatus.value.scrapeJobHunters = {
          status: "running",
          message: "Running...",
        };
        adminToolsLogs.value.unshift({
          action: "Scrape Job Hunters",
          status: "success",
          message:
            response.data.message ||
            "Job hunter scraping initiated successfully",
          timestamp: new Date().toLocaleString(),
        });
        showToastNotification(
          "success",
          "Success",
          "Job hunter scraping initiated successfully!",
        );
        // Start polling to track completion
        startStatusPolling(
          "scrapeJobHunters",
          "/admin/job-hunter-scraping-status/",
        );
      } catch (err) {
        const errorMessage =
          err.response?.data?.error || "Failed to initiate job hunter scraping";
        if (err.response?.status === 409) {
          // Already running
          adminToolsStatus.value.scrapeJobHunters = {
            status: "running",
            message: "Already running",
          };
          showToastNotification("info", "Info", errorMessage);
          // Start polling to track when it completes
          startStatusPolling(
            "scrapeJobHunters",
            "/admin/job-hunter-scraping-status/",
          );
        } else {
          adminToolsStatus.value.scrapeJobHunters = {
            status: "error",
            message: "Failed",
          };
        }
        adminToolsLogs.value.unshift({
          action: "Scrape Job Hunters",
          status: "error",
          message: errorMessage,
          timestamp: new Date().toLocaleString(),
        });
        if (err.response?.status !== 409) {
          showToastNotification("error", "Error", errorMessage);
        }
      } finally {
        adminToolsLoading.value.scrapeJobHunters = false;
      }
    };

    const runVerifyLinkedIn = async () => {
      adminToolsLoading.value.verifyLinkedIn = true;
      try {
        const response = await apiClient.post("/admin/verify-linkedin/");
        adminToolsLogs.value.unshift({
          action: "Verify LinkedIn",
          status: "success",
          message: response.data.message || "LinkedIn verification completed",
          timestamp: new Date().toLocaleString(),
        });
        showToastNotification(
          "success",
          "Success",
          "LinkedIn verification completed!",
        );
      } catch (err) {
        const errorMessage =
          err.response?.data?.error || "Failed to verify LinkedIn accounts";
        adminToolsLogs.value.unshift({
          action: "Verify LinkedIn",
          status: "error",
          message: errorMessage,
          timestamp: new Date().toLocaleString(),
        });
        showToastNotification("error", "Error", errorMessage);
      } finally {
        adminToolsLoading.value.verifyLinkedIn = false;
      }
    };

    const runSyncApplications = async () => {
      adminToolsLoading.value.syncApplications = true;
      adminToolsStatus.value.syncApplications = {
        status: "running",
        message: "Syncing...",
      };
      try {
        const response = await apiClient.post("/admin/sync-applications/");
        // Job started - now polling will track when it actually completes
        adminToolsStatus.value.syncApplications = {
          status: "running",
          message: "Running...",
        };
        adminToolsLogs.value.unshift({
          action: "Sync Applications",
          status: "success",
          message: response.data.message || "Application sync started",
          timestamp: new Date().toLocaleString(),
        });
        showToastNotification(
          "success",
          "Success",
          "Application sync started!",
        );
        // Start polling to track completion
        startStatusPolling(
          "syncApplications",
          "/admin/sync-applications-status/",
        );
      } catch (err) {
        const errorMessage =
          err.response?.data?.error || "Failed to sync applications";
        if (err.response?.status === 409) {
          // Already running
          adminToolsStatus.value.syncApplications = {
            status: "running",
            message: "Already running",
          };
          showToastNotification("info", "Info", errorMessage);
          // Start polling to track when it completes
          startStatusPolling(
            "syncApplications",
            "/admin/sync-applications-status/",
          );
        } else {
          adminToolsStatus.value.syncApplications = {
            status: "error",
            message: "Failed",
          };
        }
        adminToolsLogs.value.unshift({
          action: "Sync Applications",
          status: "error",
          message: errorMessage,
          timestamp: new Date().toLocaleString(),
        });
        if (err.response?.status !== 409) {
          showToastNotification("error", "Error", errorMessage);
        }
      } finally {
        adminToolsLoading.value.syncApplications = false;
      }
    };

    const runCleanupJobs = async () => {
      adminToolsLoading.value.cleanupJobs = true;
      adminToolsStatus.value.cleanupJobs = {
        status: "running",
        message: "Cleaning...",
      };
      try {
        const response = await apiClient.post("/admin/cleanup-jobs/");
        // Job started - now polling will track when it actually completes
        adminToolsStatus.value.cleanupJobs = {
          status: "running",
          message: "Running...",
        };
        adminToolsLogs.value.unshift({
          action: "Cleanup Jobs",
          status: "success",
          message: response.data.message || "Job cleanup started",
          timestamp: new Date().toLocaleString(),
        });
        showToastNotification("success", "Success", "Job cleanup started!");
        // Start polling to track completion
        startStatusPolling("cleanupJobs", "/admin/cleanup-status/");
      } catch (err) {
        const errorMessage =
          err.response?.data?.error || "Failed to initiate job cleanup";
        if (err.response?.status === 409) {
          // Already running
          adminToolsStatus.value.cleanupJobs = {
            status: "running",
            message: "Already running",
          };
          showToastNotification("info", "Info", errorMessage);
          // Start polling to track when it completes
          startStatusPolling("cleanupJobs", "/admin/cleanup-status/");
        } else {
          adminToolsStatus.value.cleanupJobs = {
            status: "error",
            message: "Failed",
          };
        }
        adminToolsLogs.value.unshift({
          action: "Cleanup Jobs",
          status: "error",
          message: errorMessage,
          timestamp: new Date().toLocaleString(),
        });
        if (err.response?.status !== 409) {
          showToastNotification("error", "Error", errorMessage);
        }
      } finally {
        adminToolsLoading.value.cleanupJobs = false;
      }
    };

    const runRefreshEmployerTotals = async () => {
      adminToolsLoading.value.refreshEmployers = true;
      adminToolsStatus.value.refreshEmployers = {
        status: "running",
        message: "Refreshing...",
      };
      try {
        const response = await apiClient.post(
          "/admin-dashboard/run-refresh-employers/",
        );
        adminToolsLogs.value.unshift({
          action: "Refresh Employers",
          status: "success",
          message:
            response.data.message || "Employer refresh initiated successfully",
          timestamp: new Date().toLocaleString(),
        });
        adminToolsStatus.value.refreshEmployers = {
          status: "success",
          message: "Completed",
        };
        showToastNotification(
          "success",
          "Success",
          "Employer refresh initiated successfully!",
        );
      } catch (err) {
        const errorMessage =
          err.response?.data?.error || "Failed to initiate employer refresh";
        adminToolsLogs.value.unshift({
          action: "Refresh Employers",
          status: "error",
          message: errorMessage,
          timestamp: new Date().toLocaleString(),
        });
        adminToolsStatus.value.refreshEmployers = {
          status: "error",
          message: "Failed",
        };
        showToastNotification("error", "Error", errorMessage);
      } finally {
        adminToolsLoading.value.refreshEmployers = false;
      }
    };

    const loadAdminStatus = async () => {
      try {
        const response = await apiClient.get("/admin/status/");
        adminStats.value = response.data.stats;
      } catch (err) {
        console.error("Failed to load admin status:", err);
      }
    };

    const clearAdminLogs = () => {
      adminToolsLogs.value = [];
    };

    const checkOljScrapeStatus = async () => {
      try {
        // Check scrape jobs status
        const scrapeResponse = await apiClient.get(
          "/admin-dashboard/run-olj-scraper/status/",
        );
        if (scrapeResponse?.data?.running) {
          adminToolsStatus.value.scrapeJobs = {
            status: "running",
            message: "Running...",
          };
          const lockUntil = Date.now() + 60_000;
          scrapeLockedUntil.value = lockUntil;
          persistLock(lockUntil);
          startStatusPolling(
            "scrapeJobs",
            "/admin-dashboard/run-olj-scraper/status/",
          );
        }

        // Check job hunter scraping status
        const hunterResponse = await apiClient.get(
          "/admin/job-hunter-scraping-status/",
        );
        if (hunterResponse?.data?.running) {
          adminToolsStatus.value.scrapeJobHunters = {
            status: "running",
            message: "Running...",
          };
          startStatusPolling(
            "scrapeJobHunters",
            "/admin/job-hunter-scraping-status/",
          );
        }

        // Check sync applications status
        const syncResponse = await apiClient.get(
          "/admin/sync-applications-status/",
        );
        if (syncResponse?.data?.running) {
          adminToolsStatus.value.syncApplications = {
            status: "running",
            message: "Running...",
          };
          startStatusPolling(
            "syncApplications",
            "/admin/sync-applications-status/",
          );
        }

        // Check cleanup jobs status
        const cleanupResponse = await apiClient.get("/admin/cleanup-status/");
        if (cleanupResponse?.data?.running) {
          adminToolsStatus.value.cleanupJobs = {
            status: "running",
            message: "Running...",
          };
          startStatusPolling("cleanupJobs", "/admin/cleanup-status/");
        }
      } catch (err) {
        console.error("Error checking admin tool statuses:", err);
      }
    };

    // UI state
    const personalInfoSaving = ref(false);
    const passwordSaving = ref(false);
    const notificationsSaving = ref(false);
    const integrationsSaving = ref(false);
    const oljConnectElapsedSeconds = ref(0);
    let oljConnectTimer = null;
    const privacySaving = ref(false);
    const checkingForUpdates = ref(false);
    const forceSyncing = ref(false);
    const showOljForm = ref(false);
    const dataExporting = ref(false);
    const showDisconnectOljModal = ref(false);

    /** Set to true to re-enable in-app OLJ account creation + modal. */
    const oljCreateAccountEnabled = false;

    // New OLJ connection modals
    const showOljConnectForm = ref(false);
    const showOljCreateForm = ref(false);
    const showUpdateOljForm = ref(false);

    // New OLJ account form
    const newOljAccountForm = reactive({
      email: "",
      password: "",
      confirmPassword: "",
    });

    // Update OLJ credentials form (temporary, to avoid modifying state until confirmed)
    const updateOljForm = reactive({
      email: "",
      password: "",
    });
    const showLinkedInConnectForm = ref(false);
    const showLinkedInUpdateForm = ref(false);
    const showDisconnectLinkedInModal = ref(false);
    const linkedInForm = reactive({
      email: "",
      password: "",
    });
    const updateLinkedInForm = reactive({
      email: "",
      password: "",
    });

    // JobStreet forms and state
    const showJobStreetConnectForm = ref(false);
    const showDisconnectJobStreetModal = ref(false);
    const jobstreetStep = ref(1); // 1 = email, 2 = otp
    const jobstreetTempToken = ref("");
    const jobstreetVerifying = ref(false);
    const jobstreetForm = reactive({
      email: "",
      otp: "",
    });

    const pollJobstreetTask = async (taskId, loadingMessage) => {
      // Playwright-based verification can take a while (cold start, browser launch, slow pages).
      // Use a generous timeout to avoid falsely reporting "invalid code" while the task is still running.
      const maxAttempts = 240;
      const intervalMs = 2000;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        try {
          const pollRes = await apiClient.get(`/settings/integration/jobstreet/task-status/${taskId}/`);
          const data = pollRes.data;
          if (data.status === "success") {
            return { success: true, ...data.result };
          } else if (data.status === "error") {
            return { success: false, error: data.error || "Task failed" };
          } else {
            if (attempt % 5 === 0) {
              showToastNotification("info", "Processing", loadingMessage);
            }
          }
        } catch {
          // ignore transient poll errors and retry
        }
      }
      return { success: false, timeout: true };
    };

    const pollOljTask = async (taskId, loadingMessage) => {
      // OLJ login/profile sync can be slow; keep polling long enough to avoid false failures.
      const maxAttempts = 300;
      const intervalMs = 2000;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        try {
          const pollRes = await apiClient.get(`/olj/task-status/${taskId}/`);
          const data = pollRes.data;
          console.log(`[OLJ] poll attempt ${attempt} status:`, data.status, "result:", data.result || data.error);
          if (data.status === "success") {
            console.log("[OLJ] task result:", data.result);
            return { success: true, ...data.result };
          } else if (data.status === "error") {
            console.log("[OLJ] task failed:", data.error);
            return { success: false, error: data.error || "Task failed" };
          } else if (attempt % 5 === 0) {
            showToastNotification("info", "Processing", loadingMessage);
          }
        } catch {
          // ignore transient poll errors and retry
        }
      }
      return { success: false, timeout: true };
    };

    const submitOljCredentialsAndWait = async (
      oljEmail,
      oljPassword,
      loadingMessage,
    ) => {
      const res = await apiClient.post("/olj/", {
        olj_email: oljEmail,
        olj_password: oljPassword,
        async: true,
      });
      console.log("[OLJ] connect request dispatched, task_id:", res.data && res.data.task_id);

      // Async Celery flow
      if (res.data && res.data.task_id) {
        const taskResult = await pollOljTask(res.data.task_id, loadingMessage);
        console.log("[OLJ] final task result:", taskResult);
        if (taskResult.success) {
          return taskResult;
        }
        if (taskResult.timeout) {
          console.log("[OLJ] task timed out while polling");
          const timeoutError = new Error("OLJ task is still processing.");
          timeoutError.code = "olj_task_timeout";
          throw timeoutError;
        }
        console.log("[OLJ] connect failed, status_code:", taskResult.status_code, "error:", taskResult.error);
        const error = new Error(taskResult.error || "Failed to process OLJ credentials.");
        error.response = {
          status: taskResult.status_code || 400,
          data: { error: taskResult.error || "Failed to process OLJ credentials." },
        };
        throw error;
      }

      // Legacy inline flow fallback
      return res.data;
    };

    const saveJobStreetCredentials = async () => {
      if (jobstreetStep.value === 1) {
        // Step 1: Request OTP
        integrationsSaving.value = true;
        try {
          showToastNotification("info", "Sending OTP", "Requesting verification code...");
          const res = await apiClient.post(
            "/settings/integration/jobstreet/request-otp/",
            {
              email: jobstreetForm.email,
            },
            {
              timeout: 60000,
            }
          );

          // Check if we got a task_id (async Celery flow)
          if (res.data && res.data.task_id) {
            const taskResult = await pollJobstreetTask(
              res.data.task_id,
              "Requesting OTP from JobStreet..."
            );
            if (taskResult.success) {
              jobstreetTempToken.value = taskResult.temp_token || "";
              jobstreetStep.value = 2;
              showToastNotification("success", "OTP Sent", "Check your email for the verification code.");
            } else if (taskResult.timeout) {
              showToastNotification(
                "info",
                "Still Processing",
                "OTP request is still processing. Please wait a moment then try Step 2."
              );
            } else {
              showToastNotification("error", "Error", taskResult.error || "Failed to send OTP.");
            }
          } else {
            // Legacy inline flow (DEBUG mode)
            jobstreetTempToken.value = res.data?.temp_token || "";
            jobstreetStep.value = 2;
            showToastNotification("success", "OTP Sent", "Check your email for the verification code.");
          }
        } catch (err) {
          console.error("Error requesting OTP:", err);
          const errorMessage = err?.response?.data?.error || "Failed to send OTP. Please try again.";
          showToastNotification("error", "Error", errorMessage);
        } finally {
          integrationsSaving.value = false;
        }
      } else {
        // Step 2: Verify OTP
        if (jobstreetVerifying.value) {
          return; // Prevent double-submit
        }

        // If already connected, skip verification and show success
        if (integrations.jobstreet_connected) {
          showToastNotification("success", "Already Connected", "Your JobStreet account is already connected.");
          showJobStreetConnectForm.value = false;
          jobstreetStep.value = 1;
          jobstreetForm.email = "";
          jobstreetForm.otp = "";
          jobstreetTempToken.value = "";
          return;
        }

        jobstreetVerifying.value = true;
        integrationsSaving.value = true;
        error.value = null;
        try {
          showToastNotification("info", "Verifying", "Verifying your code...");
          const res = await apiClient.post(
            "/settings/integration/jobstreet/verify-otp/",
            {
              email: jobstreetForm.email,
              otp: jobstreetForm.otp,
              temp_token: jobstreetTempToken.value,
            },
            {
              timeout: 60000,
            }
          );

          // Check if we got a task_id (async Celery flow)
          if (res.data && res.data.task_id) {
            const taskResult = await pollJobstreetTask(
              res.data.task_id,
              "Verifying OTP and connecting to JobStreet..."
            );
            if (taskResult.success && taskResult.id) {
              integrations.jobstreet_connected = true;
              integrations.jobstreet_email = jobstreetForm.email;
              integrations.jobstreet_integration_id = taskResult.id;
              showJobStreetConnectForm.value = false;
              jobstreetStep.value = 1;
              jobstreetForm.email = "";
              jobstreetForm.otp = "";
              jobstreetTempToken.value = "";
              showToastNotification("success", "Success", "JobStreet account connected successfully!");
              try {
                await loadUserSettings();
              } catch (e) {
                // Non-blocking: UI will still show connected based on local state
              }
            } else if (taskResult.timeout) {
              showToastNotification(
                "info",
                "Still Connecting",
                "Connection is still processing. Please wait a bit and check the JobStreet card; it will update once connected.",
              );
              // Background refresh attempts to pick up the eventual success without a manual page refresh.
              try {
                setTimeout(() => loadUserSettings(), 5000);
                setTimeout(() => loadUserSettings(), 15000);
                setTimeout(() => loadUserSettings(), 30000);
              } catch (e) {
                // ignore
              }
            } else {
              // Reconcile with authoritative backend state
              try {
                await loadUserSettings();
              } catch (e) {
                // ignore
              }

              if (integrations.jobstreet_connected) {
                showJobStreetConnectForm.value = false;
                jobstreetStep.value = 1;
                jobstreetForm.email = "";
                jobstreetForm.otp = "";
                jobstreetTempToken.value = "";
                showToastNotification(
                  "success",
                  "Success",
                  "JobStreet account connected successfully!",
                );
              } else {
                showToastNotification(
                  "error",
                  "Verification Failed",
                  taskResult.error || "Failed to verify OTP.",
                );
              }
            }
          } else {
            // Legacy inline flow (DEBUG mode)
            integrations.jobstreet_connected = true;
            integrations.jobstreet_email = jobstreetForm.email;
            integrations.jobstreet_integration_id = res.data?.id || null;
            showJobStreetConnectForm.value = false;
            jobstreetStep.value = 1;
            jobstreetForm.email = "";
            jobstreetForm.otp = "";
            jobstreetTempToken.value = "";

            showToastNotification("success", "Success", "JobStreet account connected successfully!");
          }

          jobstreetVerifying.value = false;
        } catch (err) {
          console.error("Error verifying OTP:", err);
          const errorMessage = err?.response?.data?.error || "Invalid code. Please try again.";
          error.value = errorMessage;
          showToastNotification("error", "Verification Failed", errorMessage);
        } finally {
          integrationsSaving.value = false;
          jobstreetVerifying.value = false;
        }
      }
    };

    const disconnectJobStreet = async () => {
      integrationsSaving.value = true;
      error.value = null;

      try {
        if (integrations.jobstreet_integration_id) {
          await apiClient.delete(
            `/settings/integration/delete/${integrations.jobstreet_integration_id}/`,
          );
        }

        integrations.jobstreet_connected = false;
        integrations.jobstreet_email = "";
        integrations.jobstreet_integration_id = null;
        showDisconnectJobStreetModal.value = false;
        jobstreetForm.email = "";
        jobstreetForm.otp = "";
        jobstreetStep.value = 1;
        jobstreetTempToken.value = "";

        showToastNotification(
          "success",
          "Success",
          "JobStreet account disconnected successfully!",
        );
      } catch (err) {
        console.error("Error disconnecting JobStreet account:", err);
        let errorMessage = "Failed to disconnect JobStreet account. Please try again later.";
        if (err?.response?.data?.error) {
          errorMessage = err.response.data.error;
        }
        error.value = errorMessage;
        showToastNotification("error", "Error", errorMessage);
      } finally {
        integrationsSaving.value = false;
      }
    };


    // Format last sync date
    const formatLastSync = (dateString) => {
      if (!dateString) return 'Never';
      
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffMins < 1) {
        return 'Just now';
      } else if (diffMins < 60) {
        return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffDays < 7) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else {
        return date.toLocaleDateString();
      }
    };

    // Check for profile updates
    const checkForProfileUpdates = async () => {
      checkingForUpdates.value = true;
      
      try {
        const response = await apiClient.post('/olj/sync-profile/');

        // HTTP 202: task queued in Celery — profile will update in the background.
        // HTTP 200: sync ran inline (DEBUG mode or Celery fallback) and completed immediately.
        if (response.data && response.data.status === 'queued') {
          showToastNotification(
            "success",
            "Sync Started",
            "OLJ profile sync has been queued. Your profile will update shortly."
          );
          // No need to reload settings immediately — the background task will
          // update the profile; user can refresh manually after a moment.
        } else if (response.data && response.data.success) {
          showToastNotification(
            "success",
            "Profile Updated",
            "Your profile has been synced with OnlineJobs.ph!"
          );

          // Inline sync finished — refresh user data to show updated profile.
          await loadUserSettings();
        } else {
          throw new Error(response.data?.error || "Failed to sync OLJ profile");
        }
      } catch (err) {
        console.error("Error syncing OLJ profile:", err);

        let errorMessage = "Failed to sync OLJ profile. Please try again.";

        if (err.response) {
          if (err.response.status === 401) {
            errorMessage = "Your OLJ session is invalid or expired. Please reconnect your OLJ account.";
          } else if (err.response.status === 400) {
            errorMessage = "OLJ account is not fully connected. Please connect or reconnect your OLJ credentials first.";
          } else if (err.response.data && err.response.data.error) {
            errorMessage = err.response.data.error;
          }
        }

        error.value = errorMessage;
        showToastNotification("error", "Error", errorMessage);
      } finally {
        checkingForUpdates.value = false;
      }
    };

    // Force sync profile (bypasses rate limits)
    const forceSyncProfile = async () => {
      forceSyncing.value = true;
      
      try {
        const response = await apiClient.post('/olj/sync-profile/', {
          force_sync: true  // Tell backend to bypass any rate limits
        });
        
        if (response.data && response.data.success) {
          showToastNotification(
            "success",
            "Force Sync Complete",
            "Your profile has been force-synced with OnlineJobs.ph! Check back in 1-2 minutes for changes."
          );
          
          // Refresh user data
          await loadUserSettings();
        } else if (response.data && response.data.status === 'queued') {
          showToastNotification(
            "info",
            "Force Sync Started",
            "Force sync has been started. OLJ may take a few minutes to update their API."
          );
        } else {
          throw new Error(response.data?.error || "Failed to force sync profile");
        }
      } catch (err) {
        console.error("Error force syncing profile:", err);
        
        let errorMessage = "Failed to force sync profile. Please try again.";

        if (err.response) {
          if (err.response.status === 401) {
            errorMessage = "Your OLJ session is invalid or expired. Please reconnect your OLJ account.";
          } else if (err.response.status === 400) {
            errorMessage = "OLJ account is not fully connected. Please connect or reconnect your OLJ credentials first.";
          } else if (err.response.data && err.response.data.error) {
            errorMessage = err.response.data.error;
          }
        }
        
        showToastNotification("error", "Error", errorMessage);
      } finally {
        forceSyncing.value = false;
      }
    };

    // Load user settings
    const loadUserSettings = async () => {
      loading.value = true;
      error.value = null;

      try {
        // Load user profile data
        const userResponse = await apiClient.get("/auth/user/");
        const userData = userResponse.data;

        if (!userData) {
          throw new Error("No user data received");
        }

        // Update user settings
        userSettings.username = userData.username || "";
        userSettings.email = userData.email || "";
        userSettings.first_name = userData.first_name || "";
        userSettings.last_name = userData.last_name || "";
        userSettings.phone_number = userData.phone_number || "";
        userSettings.account_type = userData.account_type || "jobseeker";
        userSettings.ai_instructions = userData.ai_instructions || "";

        // Load jobseeker profile data (including profile picture)
        try {
          const profileResponse = await apiClient.get("/jobseeker/profile/");
          const profileData = profileResponse.data;
          
          if (profileData && profileData.profile_picture) {
            userSettings.profile_picture = profileData.profile_picture;
          }
          if (profileData && profileData.title) {
            userSettings.title = profileData.title;
          }
          if (profileData && profileData.about) {
            userSettings.about = profileData.about;
          }
          if (profileData && profileData.skills) {
            userSettings.skills = profileData.skills;
          }
          if (profileData && profileData.personal_context != null) {
            userSettings.personal_context = profileData.personal_context;
          }
        } catch {
          // ignore, personal context is optional
        }

        userSubscriptionPlanType.value = userData.subscription?.plan?.plan_type || "free";
        userIsTrialActive.value = Boolean(userData.is_trial_active);

        try {
          // Preserve existing profile_picture when updating auth store
          const existingProfilePicture = authStore.state.user?.profile_picture;
          authStore.setAuth(true, {
            ...userData,
            profile_picture: existingProfilePicture || userData.profile_picture
          });
        } catch {
          // ignore, auth store update is best-effort here
        }

        // Load notification settings
        try {
          const notificationResponse = await apiClient.get(
            "/settings/notifications/",
          );
          if (notificationResponse.data) {
            Object.assign(notificationSettings, notificationResponse.data);
          }
        } catch (err) {
          if (err.response && err.response.status === 404) {
            // Settings don't exist yet, will be created on first save
            console.info(
              "Notification settings not created yet, using defaults",
            );
          } else {
            console.error("Error loading notification settings:", err);
          }
          // Don't set error here as it's not critical
        }

        // Load integrations
        try {
          const integrationsResponse = await apiClient.get(
            "/settings/integrations/",
          );
          const integrationsData = integrationsResponse.data;

          if (integrationsData) {
            // Check if olj_email exists in the response (backward compatibility format)
            if ("olj_email" in integrationsData) {
              integrations.olj_connected = !!integrationsData.olj_email;
              integrations.olj_email = integrationsData.olj_email || "";
              // Properly handle boolean values - only default to true if undefined/null
              integrations.olj_sync_applications =
                typeof integrationsData.olj_sync_applications === "boolean"
                  ? integrationsData.olj_sync_applications
                  : true;
              integrations.integrationId =
                integrationsData.integration_id || null;
              integrations.olj_id_proof =
                integrationsData.olj_id_proof !== undefined
                  ? integrationsData.olj_id_proof
                  : null;
            }

            // Also check the new format with integrations array (process both!)
            if (
              integrationsData.integrations &&
              Array.isArray(integrationsData.integrations)
            ) {
              // Look for OLJ integration
              const oljIntegration = integrationsData.integrations.find(
                (i) => i.type === "olj",
              );
              if (oljIntegration) {
                integrations.olj_connected = true;
                integrations.olj_email = oljIntegration.email || "";
                // Properly handle boolean values - only default to true if undefined/null
                integrations.olj_sync_applications =
                  typeof oljIntegration.sync_enabled === "boolean"
                    ? oljIntegration.sync_enabled
                    : true;
                integrations.integrationId = oljIntegration.id || null;
                integrations.olj_id_proof =
                  oljIntegration.olj_id_proof !== undefined
                    ? oljIntegration.olj_id_proof
                    : null;
                integrations.olj_connection_status =
                  oljIntegration.connection_status || "connected";
                integrations.olj_connection_status_reason =
                  oljIntegration.connection_status_reason || "";
              }

              // Look for LinkedIn integration
              const linkedinIntegration = integrationsData.integrations.find(
                (i) => i.type === "linkedin",
              );
              if (linkedinIntegration) {
                integrations.linkedin_connected = true;
                integrations.linkedin_email = linkedinIntegration.email || "";
                integrations.linkedin_integration_id =
                  linkedinIntegration.id || null;
                integrations.linkedin_sync_applications =
                  linkedinIntegration.sync_enabled !== false;
                integrations.linkedin_sync_profile =
                  linkedinIntegration.sync_profile !== false;
              }

              // Look for JobStreet integration
              const jobstreetIntegration = integrationsData.integrations.find(
                (i) => i.type === "jobstreet",
              );
              if (jobstreetIntegration) {
                integrations.jobstreet_connected = jobstreetIntegration.is_active !== false;
                integrations.jobstreet_email = jobstreetIntegration.email || "";
                integrations.jobstreet_integration_id = jobstreetIntegration.id || null;
              }
            }
          }
        } catch (err) {
          if (err.response && err.response.status === 404) {
            console.info("Integration settings not found, using defaults");
          } else {
            console.error("Error loading integration settings:", err);
          }
          // Don't set error here as it's not critical
        }

        // Load privacy settings
        try {
          const privacyResponse = await apiClient.get("/settings/privacy/");
          Object.assign(privacySettings, privacyResponse.data);
        } catch (err) {
          // Don't set error here as it's not critical
        }
      } catch (err) {
        console.error("Error loading settings:", err);
        if (err.response) {
          // Handle specific API errors
          if (err.response.status === 401) {
            error.value = "Please log in to access your settings.";
            router.push("/login");
          } else if (err.response.data && err.response.data.message) {
            error.value = err.response.data.message;
          } else {
            error.value = "Failed to load your settings. Please try again.";
          }
        } else if (err.message) {
          error.value = err.message;
        } else {
          error.value = "Failed to load your settings. Please try again.";
        }
      } finally {
        loading.value = false;
      }
    };

    // Save personal information
    const savePersonalInfo = async () => {
      personalInfoSaving.value = true;
      error.value = null;

      try {
        // Always force account_type to be 'jobseeker'
        userSettings.account_type = "jobseeker";

        const response = await apiClient.put("/user/update/", {
          username: userSettings.username,
          email: userSettings.email,
          first_name: userSettings.first_name,
          last_name: userSettings.last_name,
          phone_number: userSettings.phone_number,
          account_type: "jobseeker", // Force account type to always be jobseeker
        });

        // Save personal_context to jobseeker profile via PATCH
        try {
          await apiClient.patch("/jobseeker/profile/", {
            personal_context: userSettings.personal_context,
          });
        } catch {
          // ignore, personal context save is best-effort here
        }

        if (response.data) {
          showToastNotification(
            "success",
            "Success",
            "Personal information updated successfully!",
          );
        } else {
          throw new Error("No response data received");
        }
      } catch (err) {
        console.error("Error saving personal info:", err);
        if (err.response) {
          // Handle specific API errors
          if (err.response.status === 401) {
            error.value = "Your session has expired. Please log in again.";
            router.push("/login");
          } else if (err.response.data) {
            // Handle validation errors
            const errors = err.response.data;
            if (typeof errors === "object") {
              const errorMessages = Object.entries(errors)
                .map(([field, messages]) => {
                  // Handle both array and string error messages
                  const messageText = Array.isArray(messages)
                    ? messages.join(", ")
                    : messages;
                  return `${field}: ${messageText}`;
                })
                .join("\n");
              error.value = errorMessages;
              showToastNotification(
                "error",
                "Error",
                "Failed to update your personal information.",
              );
            } else {
              error.value = errors;
              showToastNotification(
                "error",
                "Error",
                "Failed to update your personal information.",
              );
            }
          } else {
            error.value =
              "Failed to update your personal information. Please try again.";
            showToastNotification(
              "error",
              "Error",
              "Failed to update your personal information.",
            );
          }
        } else if (err.message) {
          error.value = err.message;
          showToastNotification(
            "error",
            "Error",
            "Failed to update your personal information.",
          );
        } else {
          error.value =
            "Failed to update your personal information. Please try again.";
          showToastNotification(
            "error",
            "Error",
            "Failed to update your personal information.",
          );
        }
      } finally {
        personalInfoSaving.value = false;
      }
    };

    // Update password
    const updatePassword = async () => {
      // Validate password match
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        error.value = "New passwords do not match.";
        showToastNotification("error", "Error", "New passwords do not match.");
        return;
      }

      // Validate password strength
      const passwordRegex = /^(?=.*[A-Z])(?=.*\d).{8,}$/;
      if (!passwordRegex.test(passwordForm.newPassword)) {
        error.value =
          "Password must be at least 8 characters with at least one uppercase letter and one number.";
        showToastNotification(
          "error",
          "Error",
          "Password must be at least 8 characters with at least one uppercase letter and one number.",
        );
        return;
      }

      if (!isPasswordStrongEnough.value) {
        error.value = "Password strength is too weak.";
        showToastNotification("error", "Error", "Password strength is too weak.");
        return;
      }

      passwordSaving.value = true;
      error.value = null;

      try {

        const response = await apiClient.post("/auth/password/change/", {
          old_password: passwordForm.currentPassword,
          new_password1: passwordForm.newPassword,
          new_password2: passwordForm.confirmPassword,
        });


        // Clear password form
        passwordForm.currentPassword = "";
        passwordForm.newPassword = "";
        passwordForm.confirmPassword = "";

        showToastNotification(
          "success",
          "Success",
          response.data.message || "Password updated successfully!",
        );

        // Check if logout is required
        if (response.data.logout_required) {
          // Clear auth token
          localStorage.removeItem("token");
          apiClient.defaults.headers.common["Authorization"] = "";

          // Redirect to login page after a short delay
          setTimeout(() => {
            router.push("/login");
          }, 1500);
        }
      } catch (err) {
        console.error("Error updating password:", err);

        // Log detailed error information
        if (err.response) {
          console.error("Error response data:", err.response.data);
          console.error("Error response status:", err.response.status);
          console.error("Error response headers:", err.response.headers);
        } else if (err.request) {
          console.error("Error request:", err.request);
        } else {
          console.error("Error message:", err.message);
        }

        if (err.response && err.response.data) {
          // Handle backend validation errors
          const errors = err.response.data;
          if (errors.old_password) {
            error.value = errors.old_password[0];
            showToastNotification("error", "Error", errors.old_password[0]);
          } else if (errors.new_password1) {
            error.value = errors.new_password1[0];
            showToastNotification("error", "Error", errors.new_password1[0]);
          } else if (errors.non_field_errors) {
            error.value = errors.non_field_errors[0];
            showToastNotification("error", "Error", errors.non_field_errors[0]);
          } else {
            error.value = "Failed to update your password. Please try again.";
            showToastNotification(
              "error",
              "Error",
              "Failed to update your password.",
            );
          }
        } else {
          error.value = "Failed to update your password. Please try again.";
          showToastNotification(
            "error",
            "Error",
            "Failed to update your password.",
          );
        }
      } finally {
        passwordSaving.value = false;
      }
    };

    // Save notification settings
    const saveNotificationSettings = async () => {
      notificationsSaving.value = true;
      error.value = null;

      try {
        const response = await apiClient.put(
          "/settings/notifications/",
          notificationSettings,
        );

        if (response.data) {
          // Update local state with server response
          Object.assign(notificationSettings, response.data);
          showToastNotification(
            "success",
            "Success",
            "Notification preferences updated successfully!",
          );
        } else {
          throw new Error("No response data received");
        }
      } catch (err) {
        console.error("Error saving notification settings:", err);

        if (err.response && err.response.data) {
          // Handle backend validation errors
          const errors = err.response.data;
          if (typeof errors === "object") {
            const errorMessages = Object.entries(errors)
              .map(([field, messages]) => {
                // Handle both array and string error messages
                const messageText = Array.isArray(messages)
                  ? messages.join(", ")
                  : messages;
                return `${field}: ${messageText}`;
              })
              .join("\n");
            error.value = errorMessages;
          } else {
            error.value = errors;
          }
        } else if (err.message) {
          error.value = err.message;
        } else {
          error.value =
            "Failed to update your notification preferences. Please try again.";
        }

        showToastNotification(
          "error",
          "Error",
          "Failed to update your notification preferences.",
        );
      } finally {
        notificationsSaving.value = false;
      }
    };

    // Toggle OLJ connection
    const toggleOljConnection = () => {
      if (integrations.olj_connected) {
        // Show disconnect confirmation modal instead of alert
        showDisconnectOljModal.value = true;
      } else {
        // Show connection form
        showOljForm.value = true;
      }
    };

    // Disconnect OLJ integration
    const disconnectOlj = async () => {
      integrationsSaving.value = true;
      error.value = null;

      try {
        if (integrations.integrationId) {
          await apiClient.delete(
            `/settings/integration/delete/${integrations.integrationId}/`,
          );
        }

        integrations.olj_connected = false;
        integrations.olj_email = "";
        integrations.olj_password = "";
        integrations.integrationId = null;
        integrations.olj_connection_status = null;
        integrations.olj_connection_status_reason = "";
        showDisconnectOljModal.value = false;

        // Force backend to update onboarding status
        try {
          await apiClient.post("/auth/check-onboarding-status/");
          const userResponse = await apiClient.get("/auth/user/");
          authStore.setAuth(true, userResponse.data);
        } catch (fetchErr) {
          console.error(
            "Error refreshing user profile after OLJ disconnect:",
            fetchErr,
          );
        }

        showToastNotification(
          "success",
          "Success",
          "OLJ account disconnected successfully!",
        );
      } catch (err) {
        console.error("Error disconnecting OLJ account:", err);

        let errorMessage =
          "OLJ is taking longer than usual to respond. Please wait or try again later.";

        if (err.response && err.response.data && err.response.data.error) {
          errorMessage = err.response.data.error;
        }

        error.value = errorMessage;
        showToastNotification("error", "Error", errorMessage);
      } finally {
        integrationsSaving.value = false;
      }
    };

    // Save OLJ credentials
    const saveOljCredentials = async () => {
      integrationsSaving.value = true;
      error.value = null;
      oljConnectElapsedSeconds.value = 0;
      oljConnectTimer = setInterval(() => {
        oljConnectElapsedSeconds.value += 1;
      }, 1000);

      try {

        // First show a message indicating we're validating credentials
        showToastNotification(
          "info",
          "Validating",
          "Verifying your OLJ credentials...",
        );

        const response = await submitOljCredentialsAndWait(
          integrations.olj_email,
          integrations.olj_password,
          "Connecting your OLJ account and syncing profile...",
        );


        if (response && response.olj_email) {
          // Update saved email from response
          integrations.olj_email = response.olj_email;
          integrations.olj_connected = true;
          // A verified login clears "needs reconnect" on the backend too
          integrations.olj_connection_status = "connected";
          integrations.olj_connection_status_reason = "";
          // Store integration ID if returned
          if (response.integration_id) {
            integrations.integrationId = response.integration_id;
          }

          // Do not open OLJ verification modal after connect (new-account flow disabled).
          // Complete verification on OnlineJobs.ph if required.
          showOljConnectForm.value = false;
          showOljConnectPassword.value = false;
          showToastNotification(
            "success",
            "Success",
            "OLJ account connected successfully!",
          );
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (err) {
        console.error("Error saving OLJ credentials:", err);

        let errorMessage =
          "OLJ is taking longer than usual to respond. Please wait or try again later.";

        if (err.code === "olj_task_timeout") {
          showToastNotification(
            "info",
            "Still Processing",
            "We're still connecting your OLJ account in the background. We'll refresh automatically.",
          );
          setTimeout(() => loadUserSettings(), 5000);
          setTimeout(() => loadUserSettings(), 15000);
          setTimeout(() => loadUserSettings(), 30000);
          return;
        }

        if (err.response) {
          console.error("Error response status:", err.response.status);
          console.error("Error response data:", err.response.data);

          if (err.response.status === 401) {
            errorMessage = OLJ_WRONG_LOGIN_MESSAGE;
          } else if (err.response.status === 404) {
            errorMessage = "The OLJ integration service is not available.";
          } else if (err.response.data && err.response.data.error) {
            errorMessage = err.response.data.error;
          }
        }

        // The wrong-login message already says to try again.
        error.value = errorMessage === OLJ_WRONG_LOGIN_MESSAGE
          ? errorMessage
          : errorMessage + " Please try again.";
        showToastNotification("error", "Connection Failed", errorMessage);

        // Reset connection status in case it was previously connected
        integrations.olj_connected = false;
      } finally {
        integrationsSaving.value = false;
        clearInterval(oljConnectTimer);
        oljConnectTimer = null;
      }
    };

    // Save integration settings
    const saveIntegrationSettings = async ({ silent = false } = {}) => {
      integrationsSaving.value = true;
      error.value = null;

      try {
        const payload = {
          olj_sync_applications: integrations.olj_sync_applications,
        };

        // Add integration_id if available for the new API format
        if (integrations.integrationId) {
          payload.integration_id = integrations.integrationId;
          payload.sync_enabled = integrations.olj_sync_applications;
        }

        await apiClient.put(
          "/settings/integrations/",
          payload,
        );


        if (!silent) {
          showToastNotification(
            "success",
            "Success",
            "Integration settings updated successfully!",
          );
        }
        return true;
      } catch (err) {
        console.error("Error saving integration settings:", err);
        let errorMessage = "Failed to update your integration settings.";

        if (err.response) {
          console.error("Error response status:", err.response.status);
          console.error("Error response data:", err.response.data);

          if (err.response.data && err.response.data.error) {
            errorMessage = err.response.data.error;
          }
        }

        error.value = errorMessage + " Please try again.";
        showToastNotification("error", "Error", errorMessage);
        return false;
      } finally {
        integrationsSaving.value = false;
      }
    };

    // Debounced auto-save for OLJ integration toggles
    const scheduleOljIntegrationAutoSave = (() => {
      let timer = null;
      let lastSnapshot = null;
      return (snapshot) => {
        lastSnapshot = snapshot;
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(async () => {
          try {
            const success = await saveIntegrationSettings({ silent: true });
            if (!success && lastSnapshot) {
              // Rollback on failure
              integrations.olj_sync_applications =
                lastSnapshot.olj_sync_applications;
            }
          } catch (e) {
            // Rollback on any error
            if (lastSnapshot) {
              integrations.olj_sync_applications =
                lastSnapshot.olj_sync_applications;
            }
          }
        }, 500);
      };
    })();

    // Watch OLJ toggle changes and auto-save
    watch(
      () => [
        integrations.olj_sync_applications,
      ],
      (newVal, oldVal) => {
        // Skip during initial loading
        if (loading.value) {
          return;
        }
        // Skip if OLJ is not connected
        if (!integrations.olj_connected) {
          return;
        }
        // Skip on initial watch trigger (oldVal is undefined on first run)
        if (!oldVal) {
          return;
        }
        // Schedule debounced auto-save with snapshot for potential rollback
        scheduleOljIntegrationAutoSave({
          olj_sync_applications: oldVal[0],
        });
      },
      { flush: "post" },
    );

    // Save privacy settings
    const savePrivacySettings = async () => {
      privacySaving.value = true;
      error.value = null;

      try {
        const response = await apiClient.put(
          "/settings/privacy/",
          privacySettings,
        );
        if (response.data && response.data.message) {
          showToastNotification("success", "Success", response.data.message);
        } else {
          showToastNotification(
            "success",
            "Success",
            "Privacy settings updated successfully!",
          );
        }
      } catch (err) {
        console.error("Error saving privacy settings:", err);
        let errorMessage =
          "Failed to update your privacy settings. Please try again.";

        if (err.response) {
          if (err.response.status === 404) {
            errorMessage =
              "Privacy settings endpoint not found. Please contact support.";
          } else if (err.response.data && err.response.data.error) {
            errorMessage = err.response.data.error;
          } else if (err.response.data) {
            // Handle validation errors
            const errors = err.response.data;
            if (typeof errors === "object") {
              const errorMessages = Object.entries(errors)
                .map(([field, messages]) => {
                  // Handle both array and string error messages
                  const messageText = Array.isArray(messages)
                    ? messages.join(", ")
                    : messages;
                  return `${field}: ${messageText}`;
                })
                .join("\n");
              errorMessage = errorMessages;
            }
          }
        }

        error.value = errorMessage;
        showToastNotification("error", "Error", errorMessage);
      } finally {
        privacySaving.value = false;
      }
    };

    // Request data export
    const requestDataExport = async () => {
      dataExporting.value = true;
      error.value = null;

      try {
        await apiClient.post("/settings/request-data-export/");
        showToastNotification(
          "success",
          "Success",
          "Your data export has been requested. You will receive an email with your data shortly.",
        );
      } catch (err) {
        console.error("Error requesting data export:", err);
        error.value = "Failed to request your data export. Please try again.";
        showToastNotification(
          "error",
          "Error",
          "Failed to request your data export.",
        );
      } finally {
        dataExporting.value = false;
      }
    };

    // Delete account
    const deleteAccount = async () => {
      deleteAccountLoading.value = true;
      try {
        await apiClient.post('/auth/delete-account/', { password: deleteAccountPassword.value });
        // Clear auth state and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete apiClient.defaults.headers.common['Authorization'];
        router.push('/login');
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to delete account. Please try again.';
        showToastNotification('error', 'Error', msg);
      } finally {
        deleteAccountLoading.value = false;
      }
    };

    // Reset OLJ form
    const resetOljForm = () => {
      integrations.olj_email = "";
      integrations.olj_password = "";
      showOljConnectPassword.value = false;
    };

    // Reset new OLJ account form
    const resetNewOljForm = () => {
      newOljAccountForm.email = "";
      newOljAccountForm.password = "";
      newOljAccountForm.confirmPassword = "";
    };

    // Create new OLJ account
    const createOljAccount = async () => {
      if (!oljCreateAccountEnabled) {
        showToastNotification(
          "info",
          "Unavailable",
          "Creating a new OLJ account from Applypa is temporarily disabled.",
        );
        return;
      }
      // Validate passwords match
      if (newOljAccountForm.password !== newOljAccountForm.confirmPassword) {
        error.value = "Passwords do not match.";
        showToastNotification("error", "Error", "Passwords do not match.");
        return;
      }

      // Validate password length
      if (newOljAccountForm.password.length < 6) {
        error.value = "Password must be at least 6 characters long.";
        showToastNotification(
          "error",
          "Error",
          "Password must be at least 6 characters long.",
        );
        return;
      }

      integrationsSaving.value = true;
      error.value = null;

      try {
        showToastNotification(
          "info",
          "Creating Account",
          "Registering your new OnlineJobs.ph account...",
        );

        const response = await apiClient.post("/olj/register/", {
          email: newOljAccountForm.email,
          password: newOljAccountForm.password,
          account_type: "jobseeker",
        });


        if (response.data && response.data.success) {
          // Check if email verification is required
          if (response.data.requires_email_verification) {
            // Set pending verification state - NOT connected yet
            integrations.olj_pending_verification = true;
            integrations.olj_pending_email = newOljAccountForm.email;
            // Store credentials temporarily for verification check
            integrations.olj_email = newOljAccountForm.email;
            integrations.olj_password = newOljAccountForm.password;

            // Clear form
            showOljCreateForm.value = false;
            resetNewOljForm();

            showToastNotification(
              "success",
              "Account Created!",
              "Your OLJ account has been created. Please check your email and verify your account before connecting.",
            );
          } else {
            // No verification needed - mark as connected immediately
            integrations.olj_connected = true;
            integrations.olj_email = newOljAccountForm.email;
            integrations.integrationId = response.data.integration?.id || null;
            integrations.olj_id_proof =
              response.data.integration?.metadata?.id_proof || null;
            integrations.olj_pending_verification = false;
            integrations.olj_pending_email = "";

            // Clear form
            showOljCreateForm.value = false;
            resetNewOljForm();

            showToastNotification(
              "success",
              "Success",
              "Your new OLJ account has been created and connected successfully!",
            );
          }
        } else {
          throw new Error(response.data.error || "Registration failed");
        }
      } catch (err) {
        console.error("Error creating OLJ account:", err);

        let errorMessage =
          "Failed to create your OnlineJobs.ph account. Please try again.";

        if (err.response) {
          console.error("Error response status:", err.response.status);
          console.error("Error response data:", err.response.data);

          if (err.response.status === 400) {
            errorMessage =
              err.response.data?.error ||
              "Invalid registration data. Please check your information.";
          } else if (err.response.status === 403) {
            errorMessage =
              err.response.data?.error ||
              "Registration is currently unavailable.";
            if (err.response.data?.requires_captcha) {
              errorMessage =
                "OLJ requires CAPTCHA verification. Please register manually on OnlineJobs.ph.";
            }
          } else if (err.response.status === 409) {
            errorMessage = "This email is already registered on OnlineJobs.ph.";
          } else if (err.response.data && err.response.data.error) {
            errorMessage = err.response.data.error;
          }
        }

        // Show error in modal with better UI
        error.value = errorMessage;
        showToastNotification("error", "Registration Failed", errorMessage);

        // Keep the form open so user can correct errors
      } finally {
        integrationsSaving.value = false;
      }
    };

    // Update existing OLJ credentials
    const updateOljCredentials = async () => {
      integrationsSaving.value = true;
      error.value = null;

      try {
        showToastNotification(
          "info",
          "Updating",
          "Verifying your new OLJ credentials...",
        );

        const response = await submitOljCredentialsAndWait(
          updateOljForm.email,
          updateOljForm.password,
          "Updating OLJ credentials and syncing profile...",
        );


        if (response && response.olj_email) {
          integrations.olj_email = response.olj_email;
          integrations.olj_password = updateOljForm.password;
          integrations.integrationId =
            response.integration_id || integrations.integrationId;
          integrations.olj_id_proof =
            response.olj_id_proof !== undefined
              ? response.olj_id_proof
              : integrations.olj_id_proof;

          showUpdateOljForm.value = false;
          updateOljForm.password = ""; // Clear password from temp form
          // Verified with OLJ, so the backend has cleared "needs reconnect"
          integrations.olj_connection_status = "connected";
          integrations.olj_connection_status_reason = "";
          showToastNotification(
            "success",
            "Success",
            "OLJ credentials updated successfully!",
          );
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (err) {
        console.error("Error updating OLJ credentials:", err);

        let errorMessage = "Failed to update your OLJ credentials.";

        if (err.code === "olj_task_timeout") {
          showToastNotification(
            "info",
            "Still Processing",
            "We're still updating your OLJ credentials in the background. We'll refresh automatically.",
          );
          setTimeout(() => loadUserSettings(), 5000);
          setTimeout(() => loadUserSettings(), 15000);
          setTimeout(() => loadUserSettings(), 30000);
          return;
        }

        if (err.response) {
          if (err.response.status === 401) {
            errorMessage =
              "Invalid OLJ credentials. Please check your email and password.";
          } else if (err.response.data && err.response.data.error) {
            errorMessage = err.response.data.error;
          }
        }

        error.value = errorMessage + " Please try again.";
        showToastNotification("error", "Update Failed", errorMessage);
      } finally {
        integrationsSaving.value = false;
      }
    };

    // Verify OLJ credentials - check if account is verified and can login
    const verifyOljCredentials = async () => {
      integrationsSaving.value = true;
      error.value = null;

      try {
        showToastNotification(
          "info",
          "Verifying",
          "Checking if your OLJ account is verified...",
        );

        // Call the verify endpoint to check if credentials work
        const response = await apiClient.post("/olj/verify/", {
          email: integrations.olj_email,
          password: integrations.olj_password,
        });


        if (response.data && response.data.verified) {
          // Credentials verified - now save them properly
          const connectResponse = await submitOljCredentialsAndWait(
            integrations.olj_email,
            integrations.olj_password,
            "Connecting verified OLJ account and syncing profile...",
          );

          if (connectResponse && connectResponse.olj_email) {
            // Mark as fully connected
            integrations.olj_connected = true;
            integrations.olj_pending_verification = false;
            integrations.integrationId =
              connectResponse.integration_id || null;
            integrations.olj_id_proof =
              connectResponse.olj_id_proof !== undefined
                ? connectResponse.olj_id_proof
                : null;

            showToastNotification(
              "success",
              "Account Verified!",
              "Your OLJ account has been verified and connected successfully!",
            );
          }
        } else {
          // Not verified yet
          showToastNotification(
            "warning",
            "Not Verified Yet",
            "Your email is not verified yet. Please check your inbox and click the verification link.",
          );
        }
      } catch (err) {
        console.error("Error verifying OLJ credentials:", err);

        let errorMessage = "Unable to verify your OLJ account.";

        if (err.code === "olj_task_timeout") {
          showToastNotification(
            "info",
            "Still Processing",
            "We're still connecting your verified OLJ account in the background. We'll refresh automatically.",
          );
          setTimeout(() => loadUserSettings(), 5000);
          setTimeout(() => loadUserSettings(), 15000);
          setTimeout(() => loadUserSettings(), 30000);
          return;
        }

        if (err.response) {
          if (err.response.status === 401) {
            errorMessage =
              "Your account is not verified yet or credentials are invalid. Please check your email for the verification link.";
          } else if (err.response.data && err.response.data.error) {
            errorMessage = err.response.data.error;
          }
        }

        showToastNotification("error", "Verification Failed", errorMessage);
      } finally {
        integrationsSaving.value = false;
      }
    };

    // Cancel pending verification and clear the state
    const cancelPendingVerification = () => {
      integrations.olj_pending_verification = false;
      integrations.olj_pending_email = "";
      integrations.olj_email = "";
      integrations.olj_password = "";
      showToastNotification(
        "info",
        "Cancelled",
        "Account connection cancelled. You can connect again after verifying your email.",
      );
    };

    // Open Update Credentials modal with current values
    const openUpdateOljForm = () => {
      updateOljForm.email = integrations.olj_email;
      updateOljForm.password = "";
      showUpdateOljForm.value = true;
    };

    const oljNeedsReconnect = computed(
      () =>
        integrations.olj_connected &&
        integrations.olj_connection_status === "needs_reconnect",
    );

    // Other pages link here with ?section=integrations&reconnect=olj to open the
    // reconnect form directly; drop the flag afterwards so a refresh doesn't reopen it.
    watch(
      () => integrations.olj_connected,
      (connected) => {
        if (!connected || route.query.reconnect !== "olj") return;
        openUpdateOljForm();
        const { reconnect, ...query } = route.query; // eslint-disable-line no-unused-vars
        router.replace({ query });
      },
    );

    // LinkedIn Integration Methods
    const disconnectLinkedIn = async () => {
      integrationsSaving.value = true;
      error.value = null;

      try {
        if (integrations.linkedin_integration_id) {
          await apiClient.delete(
            `/settings/integration/delete/${integrations.linkedin_integration_id}/`,
          );
        }

        integrations.linkedin_connected = false;
        integrations.linkedin_email = "";
        integrations.linkedin_password = "";
        integrations.linkedin_integration_id = null;
        showDisconnectLinkedInModal.value = false;

        showToastNotification(
          "success",
          "Success",
          "LinkedIn account disconnected successfully!",
        );
      } catch (err) {
        console.error("Error disconnecting LinkedIn account:", err);

        let errorMessage =
          "Failed to disconnect LinkedIn account. Please try again later.";

        if (err.response && err.response.data && err.response.data.error) {
          errorMessage = err.response.data.error;
        }

        error.value = errorMessage;
        showToastNotification("error", "Error", errorMessage);
      } finally {
        integrationsSaving.value = false;
      }
    };

    const saveLinkedInCredentials = async () => {
      if (!canUseLinkedInIntegration.value) {
        showToastNotification(
          "info",
          "Upgrade required",
          "LinkedIn integration is available on Basic and Premium plans.",
        );
        router.push("/subscription/plans");
        return;
      }
      integrationsSaving.value = true;
      error.value = null;

      try {
        showToastNotification(
          "info",
          "Validating",
          "Verifying your LinkedIn credentials...",
        );

        const response = await apiClient.post("/linkedin/connect/", {
          email: linkedInForm.email,
          password: linkedInForm.password,
        });

        if (response.data && response.data.email) {
          integrations.linkedin_email = response.data.email;
          integrations.linkedin_connected = true;
          if (response.data.integration_id) {
            integrations.linkedin_integration_id = response.data.integration_id;
          }

          showLinkedInConnectForm.value = false;
          resetLinkedInForm();

          showToastNotification(
            "success",
            "Success",
            "LinkedIn account connected successfully!",
          );
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (err) {
        console.error("Error saving LinkedIn credentials:", err);

        let errorMessage =
          "Failed to connect LinkedIn account. Please try again later.";

        if (err.response) {
          if (err.response.status === 401) {
            errorMessage =
              "Invalid LinkedIn credentials. Please check your email and password.";
          } else if (err.response.status === 429) {
            errorMessage = "Too many login attempts. Please try again later.";
          } else if (err.response.data && err.response.data.error) {
            errorMessage = err.response.data.error;
          }
        }

        error.value = errorMessage;
        showToastNotification("error", "Connection Failed", errorMessage);

        integrations.linkedin_connected = false;
      } finally {
        integrationsSaving.value = false;
      }
    };

    const updateLinkedInCredentials = async () => {
      if (!canUseLinkedInIntegration.value) {
        showToastNotification(
          "info",
          "Upgrade required",
          "LinkedIn integration is available on Basic and Premium plans.",
        );
        router.push("/subscription/plans");
        return;
      }
      integrationsSaving.value = true;
      error.value = null;

      try {
        showToastNotification(
          "info",
          "Updating",
          "Verifying your new LinkedIn credentials...",
        );

        const response = await apiClient.post("/linkedin/connect/", {
          email: updateLinkedInForm.email,
          password: updateLinkedInForm.password,
        });

        if (response.data && response.data.email) {
          integrations.linkedin_email = response.data.email;
          integrations.linkedin_connected = true;
          integrations.linkedin_integration_id =
            response.data.integration_id ||
            integrations.linkedin_integration_id;

          showLinkedInUpdateForm.value = false;
          updateLinkedInForm.password = "";

          showToastNotification(
            "success",
            "Success",
            "LinkedIn credentials updated successfully!",
          );
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (err) {
        console.error("Error updating LinkedIn credentials:", err);

        let errorMessage = "Failed to update your LinkedIn credentials.";

        if (err.response) {
          if (err.response.status === 401) {
            errorMessage =
              "Invalid LinkedIn credentials. Please check your email and password.";
          } else if (err.response.data && err.response.data.error) {
            errorMessage = err.response.data.error;
          }
        }

        error.value = errorMessage + " Please try again.";
        showToastNotification("error", "Update Failed", errorMessage);
      } finally {
        integrationsSaving.value = false;
      }
    };

    const openUpdateLinkedInForm = () => {
      updateLinkedInForm.email = integrations.linkedin_email;
      updateLinkedInForm.password = "";
      showLinkedInUpdateForm.value = true;
    };

    const resetLinkedInForm = () => {
      linkedInForm.email = "";
      linkedInForm.password = "";
    };

    const computedSettingsSections = computed(() => {
      const baseSections = [...settingsSections];
      // Integrations tab: only shown once admin enables OLJ for this user
      if (oljUnlocked.value) {
        baseSections.splice(2, 0, {
          id: "integrations",
          title: "Integrations",
          icon: "fas fa-plug",
        });
      }
      // Add Admin Tools section if user is admin
      if (isAdmin.value) {
        baseSections.push({
          id: "admin",
          title: "Admin Tools",
          icon: "fas fa-tools",
        });
      }
      return baseSections;
    });

    watch(
      () => activeSection.value,
      (section) => {
        const sectionExists = computedSettingsSections.value.some(
          (s) => s.id === section,
        );
        if (!sectionExists) {
          activeSection.value = "personal";
        }
      },
      { immediate: true },
    );

    // Watch for section query param and update activeSection
    watch(
      () => route.query.section,
      (section) => {
        if (section) {
          // Check if section exists in available sections
          const sectionExists = computedSettingsSections.value.some(
            (s) => s.id === section,
          );
          if (sectionExists) {
            activeSection.value = section;
          }
        }
      },
      { immediate: true },
    );

    // Watch for activeSection changes to load admin data when admin section is opened
    watch(
      () => activeSection.value,
      (section) => {
        if (section === "activity") {
          fetchJobActivity();
        }
        if (section === "subscription") {
          fetchSubData();
        }
        if (section === "admin" && isAdmin.value) {
          loadAdminStatus();
          hydrateLock(); // Restore scrape lock state from localStorage
          checkOljScrapeStatus(); // Check if scraping is currently running
        }
      },
      // Immediate so a direct link like /settings?section=subscription loads its data too.
      { immediate: true },
    );

    // Also watch computedSettingsSections to handle cases where sections load after route query
    watch(
      computedSettingsSections,
      (sections) => {
        const sectionFromQuery = route.query.section;
        if (
          sectionFromQuery &&
          sections.some((s) => s.id === sectionFromQuery)
        ) {
          activeSection.value = sectionFromQuery;
        }
      },
      { immediate: true },
    );

    // Load settings on component mount
    onMounted(() => {
      // Check if a specific section is requested in the URL query
      const sectionFromQuery = route.query.section;
      if (
        sectionFromQuery &&
        computedSettingsSections.value.some(
          (section) => section.id === sectionFromQuery,
        )
      ) {
        activeSection.value = sectionFromQuery;
      }

      loadUserSettings();
    });

    return {
      loading,
      error,
      activeSection,
      sectionDropdownOpen,
      computedSettingsSections,
      userSettings,
      passwordForm,
      showCurrentPassword,
      showNewPassword,
      showConfirmPassword,
      showOljConnectPassword,
      passwordStrength,
      passwordStrengthClass,
      passwordStrengthBarClass,
      passwordStrengthPercent,
      passwordRequirements,
      isPasswordStrongEnough,
      handleImageError,
      notificationSettings,
      integrations,
      jobActivity,
      privacySettings,
      personalInfoSaving,
      passwordSaving,
      notificationsSaving,
      integrationsSaving,
      oljConnectElapsedSeconds,
      privacySaving,
      showOljForm,
      oljCreateAccountEnabled,
      dataExporting,
      showToast,
      toastType,
      toastTitle,
      toastMessage,
      savePersonalInfo,
      updatePassword,
      saveNotificationSettings,
      toggleOljConnection,
      disconnectOlj,
      saveOljCredentials,
      saveIntegrationSettings,
      savePrivacySettings,
      requestDataExport,
      showDisconnectOljModal,
      showLinkedInConnectForm,
      showLinkedInUpdateForm,
      showDisconnectLinkedInModal,
      updateLinkedInForm,
      // New OLJ integration properties
      showOljConnectForm,
      showOljCreateForm,
      showUpdateOljForm,
      newOljAccountForm,
      updateOljForm,
      createOljAccount,
      updateOljCredentials,
      openUpdateOljForm,
      oljNeedsReconnect,
      resetOljForm,
      resetNewOljForm,
      verifyOljCredentials,
      cancelPendingVerification,
      // JobStreet integration properties
      showJobStreetConnectForm,
      showDisconnectJobStreetModal,
      jobstreetStep,
      jobstreetTempToken,
      jobstreetVerifying,
      jobstreetForm,
      saveJobStreetCredentials,
      disconnectJobStreet,
      // LinkedIn integration methods
      disconnectLinkedIn,
      saveLinkedInCredentials,
      updateLinkedInCredentials,
      openUpdateLinkedInForm,
      resetLinkedInForm,
      // Admin tools
      isAdmin,
      adminToolsLoading,
      adminToolsLogs,
      adminToolsStatus,
      adminStats,
      isScrapeLocked,
      scrapeMessage,
      scrapeError,
      runScrapeJobs,
      runScrapeJobHunters,
      runVerifyLinkedIn,
      runSyncApplications,
      runCleanupJobs,
      runRefreshEmployerTotals,
      runRefreshEmployers: runRefreshEmployerTotals,
      loadAdminStatus,
      checkOljScrapeStatus,
      clearAdminLogs,
      hydrateLock,
      fetchJobActivity,
      isFreeUser,
      canUseLinkedInIntegration,
      oljUnlocked,
      // OLJ sync helpers
      formatLastSync,
      checkForProfileUpdates,
      checkingForUpdates,
      forceSyncProfile,
      forceSyncing,
      // Subscription section
      subData,
      subLoading,
      subError,
      aiQuota,
      filterPresetsUsed,
      filterPresetsLimit,
      usageLoading,
      subPlanMeta,
      subFormatDate,
      subPlanFeatures,
      // Delete account
      showDeleteAccountModal,
      deleteAccountPassword,
      deleteAccountLoading,
      showDeletePassword,
      deleteAccount,
    };
  },
};
</script>

<style scoped>
</style>
